import { useState, useRef, useEffect } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { magazzinoApi, magazzinoApi2, kitApi, workOrdersApi, fornitoriApi, rdaApi, inventarioApi, valorizzazioneApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { Package, Search, Plus, Boxes, Truck, MapPin, ArrowRightLeft, Download, Upload, Trash2, Edit, Eye, List, Archive, ChevronUp, ChevronDown, X } from 'lucide-react'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const STATI_LABELS: Record<string, { label: string; cls: string }> = {
  in_magazzino_centrale: { label: 'Magazzino', cls: 'bg-blue-100 text-blue-700' },
  su_furgone: { label: 'Su Furgone', cls: 'bg-amber-100 text-amber-700' },
  installato: { label: 'Installato', cls: 'bg-green-100 text-green-700' },
  in_riparazione: { label: 'In Riparazione', cls: 'bg-orange-100 text-orange-700' },
  in_quarantena: { label: 'Quarantena', cls: 'bg-yellow-100 text-yellow-700' },
  in_rma: { label: 'RMA', cls: 'bg-red-100 text-red-700' },
  restituito_committente: { label: 'Restituito', cls: 'bg-slate-100 text-slate-600' },
  dismesso: { label: 'Dismesso', cls: 'bg-slate-100 text-slate-500' },
}

type Apparato = {
  id: number
  seriale: string
  modello: string
  marca: string
  categoria: string
  stato: string
  stato_display: string
  ubicazione?: { descrizione?: string }
  committente?: string
  committente_id?: number
}

function sortItems<T>(items: T[], field: string | null, order: 'asc' | 'desc'): T[] {
  if (!field) return items
  const f: string = field
  return [...items].sort((a: any, b: any) => {
    const av = a[f] ?? ''
    const bv = b[f] ?? ''
    // Numeri puri: confronto numerico
    const an = typeof av === 'number' ? av : NaN
    const bn = typeof bv === 'number' ? bv : NaN
    if (!isNaN(an) && !isNaN(bn)) return order === 'asc' ? an - bn : bn - an
    // Stringhe: localeCompare numerico (1, 2, 10 invece di 1, 10, 2)
    const cmp = av.toString().localeCompare(bv.toString(), undefined, { numeric: true, sensitivity: 'base' })
    return order === 'asc' ? cmp : -cmp
  })
}

function SortTh({ label, field, sortField, sortOrder, onSort, className }: {
  label: string; field: string | null
  sortField: string | null; sortOrder: 'asc' | 'desc'
  onSort: (f: string) => void; className?: string
}) {
  const active = field && sortField === field
  return (
    <th
      className={clsx(className, field ? 'cursor-pointer select-none hover:text-blue-600' : '')}
      onClick={() => field && onSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        {field && (active
          ? (sortOrder === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
          : <span className="inline-flex flex-col opacity-30" style={{ lineHeight: 0, gap: 0 }}><ChevronUp size={10} /><ChevronDown size={10} /></span>
        )}
      </span>
    </th>
  )
}

export default function Magazzino() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const { user } = useAuth()
  const isTecnico = ['tecnico', 'tecnico_senior'].includes(user?.role ?? '')
  const isReadonly = user?.role === 'readonly'

  // ── Vista ristretta per tecnici ─────────────────────────────────────────
  if (isTecnico) return <MagazzinoFurgoneTecnico />

  const [tab, setTab] = useState<'apparati' | 'furgoni' | 'articoli' | 'trasferimenti' | 'kit' | 'inventario' | 'rda' | 'fornitori'>('apparati')
  const [editArticolo, setEditArticolo] = useState<any | null>(null)
  const [showCreateArticolo, setShowCreateArticolo] = useState(false)
  const [search, setSearch] = useState('')
  const [statoFilter, setStatoFilter] = useState('')
  const [ubicazioneTipo, setUbicazioneTipo] = useState('')
  const [ubicazioneDesc, setUbicazioneDesc] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [assegnaApparato, setAssegnaApparato] = useState<Apparato | null>(null)
  const [spostaArticolo, setSpostaArticolo] = useState<any | null>(null)
  const [spostaApparatoModal, setSpostaApparatoModal] = useState<Apparato | null>(null)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [traccia, setTraccia] = useState('')
  const [tracciaResult, setTracciaResult] = useState<any>(null)
  const [tracciaSearchResults, setTracciaSearchResults] = useState<any[] | null>(null)
  const [apparatiSortBy, setApparatiSortBy] = useState<string | null>(null)
  const [apparatiSortOrder, setApparatiSortOrder] = useState<'asc' | 'desc'>('asc')
  const [selectedCommittenti, setSelectedCommittenti] = useState<number[]>([])
  const [categoriaTab, setCategoriaTab] = useState<'tutti' | 'access_point'>('tutti')
  const [furgoniSortField, setFurgoniSortField] = useState<string | null>(null)
  const [furgoniSortOrder, setFurgoniSortOrder] = useState<'asc' | 'desc'>('asc')
  const [furgoniSearch, setFurgoniSearch] = useState('')

  const toggleFurgoniSort = (field: string) => {
    if (furgoniSortField === field) {
      if (furgoniSortOrder === 'asc') setFurgoniSortOrder('desc')
      else { setFurgoniSortField(null); setFurgoniSortOrder('asc') }
    } else { setFurgoniSortField(field); setFurgoniSortOrder('asc') }
  }
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [showBulkMoveApparati, setShowBulkMoveApparati] = useState(false)
  const importRef = useRef<HTMLInputElement>(null)
  const qc = useQueryClient()

  const [sostituisciFurgone, setSostituisciFurgone] = useState<any | null>(null)
  const invalidaFurgoni = () => {
    ['magazzino-furgoni', 'magazzino-furgoni-list', 'apparati', 'magazzino-stats', 'kits', 'tecnici'].forEach(k =>
      qc.invalidateQueries({ queryKey: [k] }))
  }
  const eliminaFurgoneMut = useMutation({
    mutationFn: (targa: string) => magazzinoApi2.eliminaFurgone(targa),
    onSuccess: (res) => {
      const d = res.data
      toast.success(`Furgone ${d.targa} eliminato — rientrati ${d.apparati} apparati, ${d.articoli} materiali, ${d.kit} kit`)
      invalidaFurgoni()
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore eliminazione furgone'),
  })

  const { data: furgoniData } = useQuery({
    queryKey: ['magazzino-furgoni'],
    queryFn: () => magazzinoApi2.furgoni().then(r => r.data),
    enabled: tab === 'furgoni',
  })

  const { data: committentiList = [] } = useQuery<{ id: number; ragione_sociale: string }[]>({
    queryKey: ['magazzino-committenti-lista'],
    queryFn: () => magazzinoApi.committentiLista().then(r => r.data),
  })

  const { data: stats } = useQuery({
    queryKey: ['magazzino-stats'],
    queryFn: () => magazzinoApi.stats().then(r => r.data),
  })

  const { data: ubicazioni } = useQuery({
    queryKey: ['ubicazioni-magazzino'],
    queryFn: () => magazzinoApi.ubicazioni().then(r => r.data),
    staleTime: 60_000,
  })

  const { data: apparati, isLoading } = useQuery({
    queryKey: ['apparati', search, statoFilter, ubicazioneTipo, ubicazioneDesc, apparatiSortBy, apparatiSortOrder, selectedCommittenti, categoriaTab],
    queryFn: () => magazzinoApi.apparati({
      search: search || undefined,
      stato: statoFilter || undefined,
      ubicazione_tipo: ubicazioneTipo || undefined,
      ubicazione_descrizione: ubicazioneDesc || undefined,
      per_page: 100,
      sort_by: apparatiSortBy || undefined,
      sort_order: apparatiSortOrder,
      committenti: selectedCommittenti.length > 0 ? selectedCommittenti : undefined,
      categoria: categoriaTab !== 'tutti' ? categoriaTab : undefined,
    }).then(r => r.data),
  })

  const { data: articoliData, isLoading: articoliLoading } = useQuery({
    queryKey: ['articoli'],
    queryFn: () => magazzinoApi.articoli({ per_page: 200 }).then(r => r.data),
    enabled: tab === 'articoli',
  })

  const { data: trasfVersoCentrale = [] } = useQuery({
    queryKey: ['trasf-verso-centrale'],
    queryFn: () => magazzinoApi2.trasferimentiVersoCentrale().then(r => r.data),
    refetchInterval: 60_000,
  })
  const trasfCentraleCount: number = (trasfVersoCentrale as any[]).length

  const confermaCentraleMut = useMutation({
    mutationFn: ({ id, azione }: { id: number; azione: 'conferma' | 'rifiuta' }) =>
      magazzinoApi2.confermaTrasferimento(id, azione),
    onSuccess: (_, { azione }) => {
      toast.success(azione === 'conferma' ? 'Reso confermato — materiale rientrato in magazzino' : 'Reso rifiutato')
      qc.invalidateQueries({ queryKey: ['trasf-verso-centrale'] })
      qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
      qc.invalidateQueries({ queryKey: ['apparati'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore'),
  })

  const deleteArticoloMut = useMutation({
    mutationFn: (id: number) => magazzinoApi.deleteArticolo(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['articoli'] }); toast.success('Articolo eliminato') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => magazzinoApi.deleteApparato(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apparati'] })
      qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
      toast.success('Apparato eliminato')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      for (const id of ids) await magazzinoApi.deleteApparato(id)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apparati'] })
      qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
      setSelected(new Set())
      toast.success('Apparati eliminati')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const deleteAllMutation = useMutation({
    mutationFn: () => magazzinoApi.deleteAllApparati(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apparati'] })
      qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
      setSelected(new Set())
      toast.success('Tutti gli apparati eliminati')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const allItems: Apparato[] = apparati?.items ?? []
  const allIds = allItems.map((a: Apparato) => a.id)
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id))

  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(allIds))
  }
  const toggleOne = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const handleTraccia = async () => {
    if (!traccia) return
    try {
      const res = await kitApi.tracciaSearch(traccia)
      const data = res.data
      if (data.total_results === 1) {
        // Mostra storico movimenti per singolo risultato
        const apparatoRes = await magazzinoApi.traccia(data.results[0].seriale)
        setTracciaResult(apparatoRes.data)
        setTracciaSearchResults(null)
      } else if (data.total_results > 1) {
        setTracciaSearchResults(data.results)
        setTracciaResult(null)
      } else {
        toast.error('Nessun apparato trovato')
        setTracciaResult(null)
        setTracciaSearchResults(null)
      }
    } catch {
      toast.error('Apparato non trovato')
    }
  }

  const handleExport = async () => {
    try {
      const res = await magazzinoApi.exportXlsx()
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = 'magazzino_apparati.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Errore export') }
  }

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const fn = file.name.toLowerCase().endsWith('.csv') ? magazzinoApi.importCsv : magazzinoApi.importXlsx
      const res = await fn(file)
      qc.invalidateQueries({ queryKey: ['apparati'] })
      qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
      toast.success(`Importati ${res.data.imported} apparati (${res.data.skipped} duplicati saltati)`)
    } catch { toast.error('Errore import') }
    e.target.value = ''
  }

  return (
    <div className="space-y-4">
      {ConfirmModal}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Magazzino</h1>
          <p className="text-slate-500 text-sm">Gestione apparati</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isReadonly && selected.size > 0 && (
            <>
              <button
                onClick={() => setShowBulkMoveApparati(true)}
                className="btn-secondary flex items-center gap-1.5"
              >
                <ArrowRightLeft size={15} /> Sposta {selected.size} selezionati
              </button>
              <button
                onClick={() => confirmDelete(`Eliminare ${selected.size} apparati selezionati?`, () => bulkDeleteMutation.mutate(Array.from(selected)))}
                className="btn-danger flex items-center gap-1.5"
                disabled={bulkDeleteMutation.isPending}
              >
                <Trash2 size={15} /> Elimina {selected.size} selezionati
              </button>
            </>
          )}
          {!isReadonly && (
            <button
              onClick={() => confirmDelete('Eliminare TUTTI gli apparati? Operazione irreversibile.', () => deleteAllMutation.mutate())}
              className="btn-secondary flex items-center gap-1.5"
              style={{ color: '#ef4444' }}
              disabled={deleteAllMutation.isPending}
            >
              <Trash2 size={15} /> Cancella tutto
            </button>
          )}
          <button onClick={handleExport} className="btn-secondary flex items-center gap-1.5">
            <Download size={15} /> Export XLSX
          </button>
          {!isReadonly && (
            <label className="btn-secondary flex items-center gap-1.5 cursor-pointer">
              <Upload size={15} /> Import
              <input ref={importRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleImport} />
            </label>
          )}
          {!isReadonly && (
            <button onClick={() => setShowCreate(true)} className="btn-primary">
              <Plus size={16} /> Inserisci nuovo
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: 'Tot. Apparati', val: stats?.totale_apparati, icon: Package, cls: 'text-blue-600' },
          { label: 'In Magazzino', val: stats?.in_magazzino_centrale, icon: Boxes, cls: 'text-indigo-600' },
          { label: 'Su Furgoni', val: stats?.su_furgoni, icon: Truck, cls: 'text-amber-600' },
          { label: 'Installati', val: stats?.installati, icon: MapPin, cls: 'text-green-600' },
          { label: 'In RMA', val: stats?.in_rma, icon: ArrowRightLeft, cls: 'text-red-600' },
        ].map((s) => (
          <div key={s.label} className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">{s.label}</p>
                <p className={`text-2xl font-bold mt-0.5 ${s.cls}`}>{s.val ?? '—'}</p>
              </div>
              <s.icon size={20} className={`${s.cls} opacity-60`} />
            </div>
          </div>
        ))}
      </div>

      {/* ── Valorizzazione KPI bar ── */}
      <ValorizzazioneBar />

      {/* Tabs */}
      <div className="border-b border-slate-200 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
        <div className="flex gap-1" style={{ minWidth: 'max-content' }}>
          {([['apparati', 'Apparati', Package], ['furgoni', 'Magazzino Furgoni', Truck], ['articoli', 'Materiali', List]] as const).map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key as any)}
              className={clsx('flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              )}>
              <Icon size={15} />{label}
            </button>
          ))}
          <button onClick={() => setTab('trasferimenti')}
            className={clsx('flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              tab === 'trasferimenti' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            <ArrowRightLeft size={15} />
            Trasferimenti
            {trasfCentraleCount > 0 && (
              <span style={{ background: '#ef4444', color: '#fff', borderRadius: 99, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>
                {trasfCentraleCount}
              </span>
            )}
          </button>
          <button onClick={() => setTab('kit')}
            className={clsx('flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              tab === 'kit' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            <Archive size={15} />
            Kit
          </button>
          <button onClick={() => setTab('discrepanze' as any)}
            className={clsx('flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              tab === ('discrepanze' as any) ? 'border-red-500 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            ⚠️ Discrepanze
          </button>
          <button onClick={() => setTab('inventario')}
            className={clsx('flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              tab === 'inventario' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            🔢 Inventario
          </button>
          <button onClick={() => setTab('rda')}
            className={clsx('flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              tab === 'rda' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            🛒 RDA
          </button>
          <button onClick={() => setTab('fornitori')}
            className={clsx('flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              tab === 'fornitori' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            🏭 Fornitori
          </button>
        </div>
      </div>

      {/* ── Furgoni tab ── */}
      {tab === 'furgoni' && (
        <div className="space-y-4">
          {/* Barra di ricerca */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Cerca targa, tecnico, seriale, modello, articolo..."
              value={furgoniSearch}
              onChange={e => setFurgoniSearch(e.target.value)}
            />
            {furgoniSearch && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setFurgoniSearch('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          {(() => {
            const q = furgoniSearch.toLowerCase().trim()
            const dati = (furgoniData as any[] | undefined) ?? []
            const filtrati = q ? dati.filter((f: any) => {
              if (f.targa?.toLowerCase().includes(q)) return true
              if (f.tecnico?.toLowerCase().includes(q)) return true
              if (f.apparati?.some((a: any) =>
                a.seriale?.toLowerCase().includes(q) ||
                a.modello?.toLowerCase().includes(q) ||
                a.marca?.toLowerCase().includes(q) ||
                a.categoria?.toLowerCase().includes(q)
              )) return true
              if (f.articoli?.some((a: any) =>
                a.codice?.toLowerCase().includes(q) ||
                a.descrizione?.toLowerCase().includes(q)
              )) return true
              return false
            }) : dati
            if (!filtrati.length) return (
              <div className="card p-8 text-center text-slate-400">
                {q ? `Nessun risultato per "${furgoniSearch}"` : 'Nessun furgone con apparati assegnati'}
              </div>
            )
            const totKit = filtrati.reduce((n: number, f: any) => n + (f.kit ?? []).length, 0)
            const totApp = filtrati.reduce((n: number, f: any) => n + f.apparati.length, 0)
            return <>
            <div className="card px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span className="flex items-center gap-1.5 text-slate-600"><Truck size={15} className="text-amber-600" /> <strong>{filtrati.length}</strong> furgoni</span>
              <span className="text-slate-600"><strong className="text-purple-700">{totKit}</strong> kit totali</span>
              <span className="text-slate-600"><strong>{totApp}</strong> apparati</span>
            </div>
            {filtrati.map((f: any) => (
            <div key={f.targa} className="card overflow-hidden">
              <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                <Truck size={16} className="text-amber-600" />
                <span className="font-bold text-amber-900 font-mono">{f.targa}</span>
                <span className="text-amber-600 text-sm">— {f.tecnico}</span>
                <span className="ml-auto text-xs text-amber-600">
                  <span className="badge bg-purple-100 text-purple-700 font-semibold mr-2">{(f.kit ?? []).length} kit</span>
                  {f.apparati.length} apparati · {f.articoli.length} articoli
                </span>
                {!isReadonly && (
                  <>
                    <button
                      className="btn-ghost text-xs px-2 py-1 text-blue-700 flex items-center gap-1"
                      title="Sostituisci furgone"
                      onClick={() => setSostituisciFurgone(f)}
                    >
                      <ArrowRightLeft size={13} /> Sostituisci
                    </button>
                    <button
                      className="btn-ghost text-xs px-2 py-1 text-red-600 flex items-center gap-1"
                      title="Elimina furgone"
                      onClick={() => confirmDelete(
                        `Eliminare il furgone ${f.targa}? Verrà tolto a ${f.tecnico} e tutto il contenuto (${f.apparati.length} apparati, ${f.articoli.length} materiali) rientrerà in Magazzino Centrale.`,
                        () => eliminaFurgoneMut.mutate(f.targa),
                      )}
                    >
                      <Trash2 size={13} /> Elimina
                    </button>
                  </>
                )}
              </div>
              {f.apparati.length > 0 && (
                <div className="p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Apparati</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50/50 border-b">
                        <SortTh label="Seriale" field="seriale" sortField={furgoniSortField} sortOrder={furgoniSortOrder} onSort={toggleFurgoniSort} className="text-left px-3 py-2 text-xs text-slate-500" />
                        <SortTh label="Modello" field="modello" sortField={furgoniSortField} sortOrder={furgoniSortOrder} onSort={toggleFurgoniSort} className="text-left px-3 py-2 text-xs text-slate-500" />
                        <SortTh label="Categoria" field="categoria" sortField={furgoniSortField} sortOrder={furgoniSortOrder} onSort={toggleFurgoniSort} className="text-left px-3 py-2 text-xs text-slate-500" />
                        <th className="text-center px-3 py-2 text-xs text-slate-500">Azioni</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-50">
                        {sortItems(f.apparati, furgoniSortField, furgoniSortOrder).map((a: any) => (
                          <tr key={a.id} className="hover:bg-slate-50">
                            <td className="px-3 py-2 font-mono text-xs text-blue-700">{a.seriale}</td>
                            <td className="px-3 py-2 text-xs text-slate-700">{a.modello || '—'} {a.marca && <span className="text-slate-400">({a.marca})</span>}</td>
                            <td className="px-3 py-2 text-xs text-slate-500 capitalize">{a.categoria?.replace(/_/g, ' ') || '—'}</td>
                            <td className="px-3 py-2">
                              <SpostaApparatoBtn apparatoId={a.id} targa={f.targa} onSuccess={() => {
                                qc.invalidateQueries({ queryKey: ['magazzino-furgoni'] })
                                qc.invalidateQueries({ queryKey: ['magazzino-furgoni-list'] })
                                qc.invalidateQueries({ queryKey: ['apparati'] })
                                qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
                                toast.success('Apparato restituito al magazzino')
                              }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {f.articoli.length > 0 && (
                <div className="px-4 pb-4 border-t">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide my-2">Materiali di consumo</p>
                  <div className="flex flex-wrap gap-2">
                    {f.articoli.map((a: any) => (
                      <span key={a.codice} className="badge bg-slate-100 text-slate-600 text-xs">
                        {a.codice} — {a.descrizione}: <strong>{a.quantita} {a.unita_misura}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            ))}
            </>
          })()}
        </div>
      )}

      {sostituisciFurgone && (
        <SostituisciFurgoneModal
          furgone={sostituisciFurgone}
          onClose={() => setSostituisciFurgone(null)}
          onSuccess={() => { setSostituisciFurgone(null); invalidaFurgoni() }}
        />
      )}

      {/* ── Apparati tab ── */}
      {tab === 'apparati' && <>

      {/* Tracciabilità */}
      <div className="card p-4">
        <p className="text-sm font-medium text-slate-700 mb-2">Traccia Apparato</p>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="Cerca apparato (nome, seriale, categoria, stato...)"
            value={traccia}
            onChange={(e) => { setTraccia(e.target.value); if (!e.target.value) { setTracciaResult(null); setTracciaSearchResults(null) } }}
            onKeyDown={(e) => e.key === 'Enter' && handleTraccia()}
          />
          <button onClick={handleTraccia} className="btn-primary">Traccia</button>
          {(tracciaResult || tracciaSearchResults) && (
            <button onClick={() => { setTracciaResult(null); setTracciaSearchResults(null); setTraccia('') }} className="btn-ghost p-2 text-slate-400"><X size={14} /></button>
          )}
        </div>
        {tracciaSearchResults && tracciaSearchResults.length > 1 && (
          <div className="mt-3 space-y-1">
            <p className="text-xs text-slate-500 mb-2">{tracciaSearchResults.length} apparati trovati — clicca per vedere i movimenti</p>
            {tracciaSearchResults.map((a: any) => (
              <div
                key={a.id}
                className="p-2 bg-slate-50 rounded-lg text-sm flex items-center justify-between cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={async () => {
                  try {
                    const res = await magazzinoApi.traccia(a.seriale)
                    setTracciaResult(res.data)
                    setTracciaSearchResults(null)
                  } catch { toast.error('Errore caricamento dettaglio') }
                }}
              >
                <div>
                  <span className="font-mono font-medium text-blue-700">{a.seriale}</span>
                  {a.modello && <span className="text-slate-500 ml-2">{a.modello}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 capitalize">{a.categoria}</span>
                  <span className={clsx('badge text-xs', STATI_LABELS[a.stato]?.cls || 'bg-slate-100')}>
                    {STATI_LABELS[a.stato]?.label || a.stato}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        {tracciaResult && (
          <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
            <div className="font-medium text-slate-800 mb-1">{tracciaResult.apparato.seriale} — {tracciaResult.apparato.modello}</div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Stato:</span>
              <span className={clsx('badge', STATI_LABELS[tracciaResult.apparato.stato_attuale]?.cls || 'bg-slate-100')}>
                {STATI_LABELS[tracciaResult.apparato.stato_attuale]?.label || tracciaResult.apparato.stato_attuale}
              </span>
            </div>
            <div className="text-xs text-slate-400 mt-1">{tracciaResult.storico_movimenti?.length || 0} movimenti registrati</div>
          </div>
        )}
      </div>

      {/* ── Filtro Committenti ─────────────────────────────────────────── */}
      {committentiList.length > 0 && (
        <div className="card p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Committente</span>
            <div className="flex flex-wrap gap-2">
              {committentiList.map((c) => (
                <label key={c.id} className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={selectedCommittenti.includes(c.id)}
                    onChange={(e) => {
                      setSelected(new Set())
                      setSelectedCommittenti(prev =>
                        e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id)
                      )
                    }}
                    className="w-3.5 h-3.5 accent-blue-600"
                  />
                  <span className="text-sm text-slate-700">{c.ragione_sociale}</span>
                </label>
              ))}
            </div>
            {selectedCommittenti.length > 0 && (
              <button
                onClick={() => { setSelectedCommittenti([]); setSelected(new Set()) }}
                className="text-xs text-blue-600 hover:underline whitespace-nowrap"
              >
                Deseleziona tutto
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Sub-tab categoria ──────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-slate-200">
        {([
          { key: 'tutti', label: 'Tutti gli apparati' },
          { key: 'access_point', label: 'AP WiFi (access_point)' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setCategoriaTab(key); setSelected(new Set()) }}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              categoriaTab === key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search + filter */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input pl-9" placeholder="Cerca seriale, modello..." value={search} onChange={(e) => { setSearch(e.target.value); setSelected(new Set()) }} />
        </div>
        <select className="select w-44" value={statoFilter} onChange={(e) => { setStatoFilter(e.target.value); setSelected(new Set()) }}>
          <option value="">Tutti gli stati</option>
          {Object.entries(STATI_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="select w-40" value={ubicazioneTipo} onChange={(e) => { setUbicazioneTipo(e.target.value); setUbicazioneDesc(''); setSelected(new Set()) }}>
          <option value="">Tutte le ubicazioni</option>
          <option value="magazzino_centrale">Magazzino</option>
          <option value="furgone">Furgone</option>
          <option value="sito">Sito</option>
        </select>
        {ubicazioneTipo && (
          <select className="select w-44" value={ubicazioneDesc} onChange={(e) => { setUbicazioneDesc(e.target.value); setSelected(new Set()) }}>
            <option value="">— Tutte —</option>
            {(ubicazioni as any[] ?? []).filter((u: any) => u.tipo === ubicazioneTipo).map((u: any) => (
              <option key={u.descrizione} value={u.descrizione}>{u.descrizione} ({u.count})</option>
            ))}
          </select>
        )}
        {(ubicazioneTipo || statoFilter) && (
          <button className="btn-ghost text-xs text-slate-500" onClick={() => { setUbicazioneTipo(''); setUbicazioneDesc(''); setStatoFilter(''); setSelected(new Set()) }}>
            ✕ Reset
          </button>
        )}
        <button
          onClick={toggleAll}
          className="btn-secondary text-xs"
          style={{ padding: '6px 12px' }}
        >
          {allSelected ? 'Deseleziona tutto' : 'Seleziona tutto'}
        </button>
        {selected.size > 0 && (
          <span className="text-xs text-slate-500">{selected.size} selezionati</span>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="text-sm" style={{ minWidth: 760 }}>
          <thead>
            <tr className="border-b bg-slate-50/50">
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Seleziona tutto" />
              </th>
              {([
                ['seriale', 'Seriale'],
                ['modello', 'Modello'],
                ['categoria', 'Categoria'],
                ['stato', 'Stato'],
                [null, 'Ubicazione'],
                [null, 'Kit'],
                [null, 'Committente'],
              ] as [string | null, string][]).map(([field, label]) => (
                <th
                  key={label}
                  className={clsx('text-left px-4 py-3 font-semibold text-slate-600 select-none', field && 'cursor-pointer hover:text-blue-600')}
                  onClick={() => {
                    if (!field) return
                    if (apparatiSortBy === field) {
                      if (apparatiSortOrder === 'asc') setApparatiSortOrder('desc')
                      else { setApparatiSortBy(null); setApparatiSortOrder('asc') }
                    } else {
                      setApparatiSortBy(field)
                      setApparatiSortOrder('asc')
                    }
                  }}
                >
                  <span className="flex items-center gap-1">
                    {label}
                    {field && apparatiSortBy === field && (
                      apparatiSortOrder === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                    )}
                  </span>
                </th>
              ))}
              <th className="text-center px-4 py-3 font-semibold text-slate-600">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400">Caricamento...</td></tr>
            ) : (apparati?.items || []).length === 0 ? (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400">Nessun apparato trovato</td></tr>
            ) : (apparati?.items || []).map((a: Apparato) => {
              const statoLabel = STATI_LABELS[a.stato]
              const isSelected = selected.has(a.id)
              return (
                <tr key={a.id} className="table-row-hover" style={{ background: isSelected ? 'rgba(26,115,232,0.05)' : undefined }}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleOne(a.id)} />
                  </td>
                  <td className="px-4 py-3 font-mono text-sm font-medium text-blue-700">{a.seriale}</td>
                  <td className="px-4 py-3">
                    <div className="text-slate-800">{a.modello || '—'}</div>
                    {a.marca && <div className="text-xs text-slate-400">{a.marca}</div>}
                  </td>
                  <td className="px-4 py-3 capitalize text-slate-600">{a.categoria}</td>
                  <td className="px-4 py-3">
                    {a.stato_display && a.stato_display !== a.stato ? (
                      <span className={clsx('badge', statoLabel?.cls || 'bg-slate-100')} title={a.stato}>
                        {a.stato_display}
                      </span>
                    ) : (
                      <span className={clsx('badge', statoLabel?.cls || 'bg-slate-100')}>
                        {statoLabel?.label || a.stato}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(a as any).ubicazione?.descrizione || '—'}</td>
                  <td className="px-4 py-3 text-xs">
                    {(a as any).kit ? (
                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium">{(a as any).kit}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{a.committente || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => setDetailId(a.id)}
                        className="btn-ghost px-2 py-1 text-xs text-blue-600"
                        title="Dettaglio"
                      >
                        <Eye size={13} />
                      </button>
                      {!isReadonly && (
                        <>
                          <button
                            onClick={() => setSpostaApparatoModal(a)}
                            className="btn-ghost px-2 py-1 text-xs text-blue-600"
                            title="Sposta"
                          >
                            <ArrowRightLeft size={13} />
                          </button>
                          <button
                            onClick={() => setAssegnaApparato(a)}
                            className="btn-ghost px-2 py-1 text-xs"
                            title="Assegna"
                          >
                            <Edit size={13} />
                          </button>
                          <button
                            onClick={() => confirmDelete(`Eliminare ${a.seriale}?`, () => deleteMutation.mutate(a.id))}
                            className="btn-ghost px-2 py-1 text-red-500"
                            title="Elimina"
                          >
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
        {apparati && (
          <div className="px-4 py-2 border-t text-xs text-slate-400">
            {apparati.total} apparati totali
          </div>
        )}
      </div>

      {showCreate && (
        <CreateApparatoModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false)
            qc.invalidateQueries({ queryKey: ['apparati'] })
            qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
            toast.success('Apparato caricato!')
          }}
        />
      )}

      {detailId !== null && (
        <ApparatoDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
          onSuccess={() => {
            setDetailId(null)
            qc.invalidateQueries({ queryKey: ['apparati'] })
            qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
            toast.success('Apparato aggiornato')
          }}
        />
      )}

      {assegnaApparato && (
        <AssegnaModal
          apparato={assegnaApparato}
          onClose={() => setAssegnaApparato(null)}
          onSuccess={() => {
            setAssegnaApparato(null)
            qc.invalidateQueries({ queryKey: ['apparati'] })
            toast.success('Apparato assegnato!')
          }}
        />
      )}

      {spostaApparatoModal && (
        <SpostaApparatoModal
          apparato={spostaApparatoModal}
          onClose={() => setSpostaApparatoModal(null)}
          onSuccess={() => {
            setSpostaApparatoModal(null)
            qc.invalidateQueries({ queryKey: ['apparati'] })
            qc.invalidateQueries({ queryKey: ['magazzino-furgoni'] })
            qc.invalidateQueries({ queryKey: ['magazzino-furgoni-list'] })
            qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
            toast.success('Apparato spostato!')
          }}
        />
      )}

      {showBulkMoveApparati && (
        <BulkMoveApparatiModal
          ids={Array.from(selected)}
          onClose={() => setShowBulkMoveApparati(false)}
          onSuccess={() => {
            setShowBulkMoveApparati(false)
            setSelected(new Set())
            qc.invalidateQueries({ queryKey: ['apparati'] })
            qc.invalidateQueries({ queryKey: ['magazzino-furgoni'] })
            qc.invalidateQueries({ queryKey: ['magazzino-furgoni-list'] })
            qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
          }}
        />
      )}
      </>}

      {/* ── Articoli tab ── */}
      {tab === 'articoli' && (
        <div className="space-y-4">
          {!isReadonly && (
            <div className="flex justify-end">
              <button onClick={() => setShowCreateArticolo(true)} className="btn-primary">
                <Plus size={16} /> Nuovo Articolo
              </button>
            </div>
          )}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
            <table className="text-sm" style={{ minWidth: 700 }}>
              <thead>
                <tr className="border-b bg-slate-50/50">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Codice</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Descrizione</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Categoria</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">U.M.</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600">Giacenza</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600">Min.</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {articoliLoading ? (
                  <tr><td colSpan={7} className="py-10 text-center text-slate-400">Caricamento...</td></tr>
                ) : (articoliData?.items ?? []).length === 0 ? (
                  <tr><td colSpan={7} className="py-10 text-center text-slate-400">Nessun articolo trovato</td></tr>
                ) : (articoliData?.items ?? []).map((a: any) => (
                  <tr key={a.id} className="table-row-hover">
                    <td className="px-4 py-3 font-mono text-xs text-blue-700 font-medium">{a.codice}</td>
                    <td className="px-4 py-3 text-slate-800">{a.descrizione}</td>
                    <td className="px-4 py-3 text-slate-500 capitalize text-xs">{a.categoria?.replace(/_/g, ' ') || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{a.unita_misura}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={clsx('font-semibold', a.giacenza_centrale <= (a.soglia_minima_riordino || 0) && a.giacenza_centrale > 0 ? 'text-amber-600' : a.giacenza_centrale === 0 ? 'text-red-600' : 'text-slate-800')}>
                        {a.giacenza_centrale ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-400 text-xs">{a.soglia_minima_riordino ?? 0}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex gap-1 justify-center">
                        {!isReadonly && (
                          <>
                            <button onClick={() => setSpostaArticolo(a)} className="btn-ghost px-2 py-1 text-xs text-blue-600" title="Trasferisci"><ArrowRightLeft size={13} /></button>
                            <button onClick={() => setEditArticolo(a)} className="btn-ghost px-2 py-1 text-amber-600" title="Modifica"><Edit size={13} /></button>
                            <button onClick={() => confirmDelete(`Eliminare ${a.codice}?`, () => deleteArticoloMut.mutate(a.id))} className="btn-ghost px-2 py-1 text-red-500" title="Elimina"><Trash2 size={13} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Trasferimenti verso centrale tab ── */}
      {tab === 'trasferimenti' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Materiali e apparati inviati dai tecnici in attesa di essere ricevuti al magazzino centrale.
            </p>
          </div>
          {trasfCentraleCount === 0 ? (
            <div className="card p-12 text-center text-slate-400">
              <ArrowRightLeft size={36} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium text-sm">Nessuna restituzione in attesa</p>
            </div>
          ) : (trasfVersoCentrale as any[]).map((t: any) => (
            <div key={t.id} className="card p-4 flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium', t.tipo === 'apparato' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700')}>
                    {t.tipo === 'apparato' ? 'Apparato' : 'Articolo'}
                  </span>
                  <span className="font-semibold text-sm text-slate-800">
                    {t.item_nome ?? `ID ${t.item_id}`}
                  </span>
                  {t.quantita !== 1 && (
                    <span className="text-xs text-slate-500">× {t.quantita}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Da furgone: <span className="font-mono font-medium">{t.da_targa || '—'}</span>
                  {t.created_at && (
                    <> · {new Date(t.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => confermaCentraleMut.mutate({ id: t.id, azione: 'rifiuta' })}
                  disabled={confermaCentraleMut.isPending}
                  className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                  ✕ Rifiuta
                </button>
                <button
                  onClick={() => confermaCentraleMut.mutate({ id: t.id, azione: 'conferma' })}
                  disabled={confermaCentraleMut.isPending}
                  className="btn-primary text-sm px-3 py-1.5">
                  ✓ Ricevuto
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Kit tab ── */}
      {tab === 'kit' && <KitTab />}

      {/* ── Discrepanze tab ── */}
      {tab === ('discrepanze' as any) && <DiscrepanzeTab />}

      {/* ── Inventario fisico tab ── */}
      {tab === 'inventario' && <TabInventario />}

      {/* ── RDA tab ── */}
      {tab === 'rda' && <TabRDA />}

      {/* ── Fornitori tab ── */}
      {tab === 'fornitori' && <TabFornitori />}

      {(editArticolo || showCreateArticolo) && (
        <ArticoloModal
          articolo={editArticolo}
          onClose={() => { setEditArticolo(null); setShowCreateArticolo(false) }}
          onSuccess={() => {
            setEditArticolo(null)
            setShowCreateArticolo(false)
            qc.invalidateQueries({ queryKey: ['articoli'] })
            toast.success(editArticolo ? 'Articolo aggiornato!' : 'Articolo creato!')
          }}
        />
      )}
      {spostaArticolo && (
        <SpostaArticoloModal
          articolo={spostaArticolo}
          onClose={() => setSpostaArticolo(null)}
          onSuccess={() => {
            setSpostaArticolo(null)
            qc.invalidateQueries({ queryKey: ['articoli'] })
            qc.invalidateQueries({ queryKey: ['magazzino-furgoni'] })
            qc.invalidateQueries({ queryKey: ['magazzino-furgoni-list'] })
            qc.invalidateQueries({ queryKey: ['magazzino-stats'] })
            toast.success('Trasferimento effettuato!')
          }}
        />
      )}
    </div>
  )
}

// ─── KitTab ─────────────────────────────────────────────────────────────────

function KitTab() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const { user } = useAuth()
  const qc = useQueryClient()
  const canWrite = ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico'].includes(user?.role ?? '')

  const [showCreate, setShowCreate] = useState(false)
  const [editKit, setEditKit] = useState<any | null>(null)
  const [manageKit, setManageKit] = useState<any | null>(null)
  const [transferKit, setTransferKit] = useState<any | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [showBulkTransferKits, setShowBulkTransferKits] = useState(false)
  const importRef = useRef<HTMLInputElement>(null)
  const [selectedKits, setSelectedKits] = useState<Set<number>>(new Set())
  const [kitSortField, setKitSortField] = useState<string | null>(null)
  const [kitSortOrder, setKitSortOrder] = useState<'asc' | 'desc'>('asc')
  const [kitSearch, setKitSearch] = useState('')
  const [kitStatoFilter, setKitStatoFilter] = useState('')

  const toggleKitSort = (field: string) => {
    if (kitSortField === field) {
      if (kitSortOrder === 'asc') setKitSortOrder('desc')
      else { setKitSortField(null); setKitSortOrder('asc') }
    } else { setKitSortField(field); setKitSortOrder('asc') }
  }

  const { data: kits = [], isLoading } = useQuery({
    queryKey: ['kits', kitSearch, kitStatoFilter],
    queryFn: () => kitApi.list({ search: kitSearch || undefined, stato: kitStatoFilter || undefined }).then(r => r.data),
  })

  const allKitIds = (kits as any[]).map((k: any) => k.id)
  const allKitsSelected = allKitIds.length > 0 && allKitIds.every((id: number) => selectedKits.has(id))

  const toggleAllKits = () => {
    if (allKitsSelected) {
      setSelectedKits(new Set())
    } else {
      setSelectedKits(new Set(allKitIds))
    }
  }

  const toggleOneKit = (id: number) => {
    setSelectedKits(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const deleteMut = useMutation({
    mutationFn: (id: number) => kitApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['kits'] }); toast.success('Kit eliminato') },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore eliminazione'),
  })

  const bulkDeleteKitsMut = useMutation({
    mutationFn: (ids: number[]) => kitApi.bulkDelete(ids),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['kits'] })
      setSelectedKits(new Set())
      toast.success(`${res.data.deleted} kit eliminati`)
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore eliminazione massiva'),
  })

  const handleExportKit = async () => {
    try {
      const res = await kitApi.exportXlsx()
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `kit_export_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Export completato')
    } catch { toast.error('Errore export') }
  }

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const res = await kitApi.importXlsx(file)
      const d = res.data
      qc.invalidateQueries({ queryKey: ['kits'] })
      qc.invalidateQueries({ queryKey: ['articoli'] })
      let msg = `Importati ${d.kits_imported} kit, ${d.materiali_imported} materiali`
      if (d.articoli_creati > 0) msg += ` (${d.articoli_creati} articoli creati in magazzino)`
      toast.success(msg)
      if (d.warnings?.length) d.warnings.forEach((w: string) => toast(w, { icon: '⚠️' }))
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Errore import')
    }
    e.target.value = ''
  }

  return (
    <div className="space-y-4">
      {ConfirmModal}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">Gestione kit: raggruppa apparati e materiali in bundle riutilizzabili.</p>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleExportKit} className="btn-secondary flex items-center gap-1.5 text-sm">
              <Download size={14} /> Esporta XLSX
            </button>
            <label className="btn-secondary flex items-center gap-1.5 text-sm cursor-pointer">
              <Upload size={14} /> Importa XLSX
              <input ref={importRef} type="file" accept=".xlsx" className="hidden" onChange={handleImportFile} />
            </label>
            <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-sm">
              <Plus size={14} /> Nuovo Kit
            </button>
          </div>
        )}
      </div>

      {canWrite && selectedKits.size > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-500">{selectedKits.size} kit selezionati</span>
          <button
            onClick={() => setShowBulkTransferKits(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-amber-500 text-white hover:bg-amber-600"
          >
            <ArrowRightLeft size={14} /> Sposta {selectedKits.size} kit
          </button>
          <button
            onClick={() => {
              confirmDelete(`Eliminare ${selectedKits.size} kit selezionati? Operazione irreversibile.`, () => bulkDeleteKitsMut.mutate(Array.from(selectedKits)))
            }}
            disabled={bulkDeleteKitsMut.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
          >
            <Trash2 size={14} /> Elimina {selectedKits.size} selezionati
          </button>
          <button onClick={() => setSelectedKits(new Set())} className="btn-ghost text-sm text-slate-500 flex items-center gap-1">
            <X size={14} /> Deseleziona
          </button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input pl-9" placeholder="Cerca kit..." value={kitSearch} onChange={e => setKitSearch(e.target.value)} />
        </div>
        <select className="select w-40" value={kitStatoFilter} onChange={e => setKitStatoFilter(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="disponibile">Disponibile</option>
          <option value="su_furgone">Su furgone</option>
          <option value="in_magazzino_centrale">Magazzino centrale</option>
          <option value="installato">Installato</option>
          <option value="in_verifica">In verifica</option>
        </select>
        {(kitSearch || kitStatoFilter) && (
          <button className="btn-ghost text-xs text-slate-500" onClick={() => { setKitSearch(''); setKitStatoFilter('') }}>
            ✕ Reset
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm" style={{ minWidth: 600 }}>
            <thead>
              <tr className="border-b bg-slate-50/50">
                {canWrite && (
                  <th className="px-4 py-3 w-8">
                    <input type="checkbox" checked={allKitsSelected} onChange={toggleAllKits} title="Seleziona tutti" />
                  </th>
                )}
                <SortTh label="Nome" field="nome" sortField={kitSortField} sortOrder={kitSortOrder} onSort={toggleKitSort} className="text-left px-4 py-3 font-semibold text-slate-600" />
                <SortTh label="Descrizione" field="descrizione" sortField={kitSortField} sortOrder={kitSortOrder} onSort={toggleKitSort} className="text-left px-4 py-3 font-semibold text-slate-600" />
                <SortTh label="Stato" field="stato" sortField={kitSortField} sortOrder={kitSortOrder} onSort={toggleKitSort} className="text-left px-4 py-3 font-semibold text-slate-600" />
                <SortTh label="Ubicazione" field="ubicazione" sortField={kitSortField} sortOrder={kitSortOrder} onSort={toggleKitSort} className="text-left px-4 py-3 font-semibold text-slate-600" />
                <SortTh label="Materiali" field="materiali_count" sortField={kitSortField} sortOrder={kitSortOrder} onSort={toggleKitSort} className="text-center px-4 py-3 font-semibold text-slate-600" />
                <SortTh label="Attivo" field="attivo" sortField={kitSortField} sortOrder={kitSortOrder} onSort={toggleKitSort} className="text-center px-4 py-3 font-semibold text-slate-600" />
                <th className="text-center px-4 py-3 font-semibold text-slate-600">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={canWrite ? 8 : 7} className="py-10 text-center text-slate-400">Caricamento...</td></tr>
              ) : (kits as any[]).length === 0 ? (
                <tr><td colSpan={canWrite ? 8 : 7} className="py-12 text-center text-slate-400">
                  <Archive size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nessun kit presente</p>
                </td></tr>
              ) : sortItems(kits as any[], kitSortField, kitSortOrder).map((k: any) => (
                <tr key={k.id} className={clsx('table-row-hover', selectedKits.has(k.id) && 'bg-blue-50')}>
                  {canWrite && (
                    <td className="px-4 py-3 w-8">
                      <input type="checkbox" checked={selectedKits.has(k.id)} onChange={() => toggleOneKit(k.id)} />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium text-slate-800">
                    <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-mono">{k.nome}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-sm">{k.descrizione || '—'}</td>
                  <td className="px-4 py-3">
                    {k.stato ? (
                      <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', {
                        'bg-green-100 text-green-700': k.stato === 'in_magazzino_centrale',
                        'bg-amber-100 text-amber-700': k.stato === 'su_furgone',
                        'bg-blue-100 text-blue-700': k.stato === 'in_uso',
                        'bg-red-100 text-red-700': k.stato === 'in_manutenzione',
                        'bg-slate-100 text-slate-600': !['in_magazzino_centrale','su_furgone','in_uso','in_manutenzione'].includes(k.stato),
                      })}>
                        {k.stato.replace(/_/g, ' ')}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-sm max-w-[160px] truncate" title={k.ubicazione || ''}>{k.ubicazione || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">{k.materiali_count}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-lg">{k.attivo ? '✓' : '✗'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => setManageKit(k)}
                        className="btn-ghost px-2 py-1 text-xs text-blue-600"
                        title="Gestisci materiali"
                      ><Edit size={13} /></button>
                      {canWrite && (
                        <>
                          <button
                            onClick={() => setTransferKit(k)}
                            className="btn-ghost px-2 py-1 text-xs text-amber-600"
                            title="Trasferisci"
                          ><ArrowRightLeft size={13} /></button>
                          <button
                            onClick={() => setEditKit(k)}
                            className="btn-ghost px-2 py-1 text-xs text-slate-500"
                            title="Modifica metadata"
                          ><Eye size={13} /></button>
                          <button
                            onClick={() => confirmDelete(`Eliminare kit "${k.nome}"?`, () => deleteMut.mutate(k.id))}
                            className="btn-ghost px-2 py-1 text-red-500"
                            title="Elimina"
                          ><Trash2 size={13} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <KitCreateModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['kits'] }); toast.success('Kit creato') }}
        />
      )}
      {editKit && (
        <KitEditModal
          kit={editKit}
          onClose={() => setEditKit(null)}
          onSuccess={() => { setEditKit(null); qc.invalidateQueries({ queryKey: ['kits'] }) }}
        />
      )}
      {manageKit && (
        <KitMaterialiModal
          kit={manageKit}
          canWrite={canWrite}
          onClose={() => setManageKit(null)}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['kits'] })}
        />
      )}
      {transferKit && (
        <KitTransferModal
          kit={transferKit}
          onClose={() => setTransferKit(null)}
          onSuccess={() => { setTransferKit(null); qc.invalidateQueries({ queryKey: ['kits'] }); toast.success('Kit trasferito') }}
        />
      )}

      {showBulkTransferKits && (
        <BulkTransferKitsModal
          ids={Array.from(selectedKits)}
          onClose={() => setShowBulkTransferKits(false)}
          onSuccess={() => {
            setShowBulkTransferKits(false)
            setSelectedKits(new Set())
            qc.invalidateQueries({ queryKey: ['kits'] })
            qc.invalidateQueries({ queryKey: ['apparati'] })
            qc.invalidateQueries({ queryKey: ['magazzino-furgoni'] })
          }}
        />
      )}
    </div>
  )
}

const KIT_STATI = ['in_magazzino_centrale', 'su_furgone', 'in_uso', 'in_manutenzione']

function KitCreateModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [nome, setNome] = useState('')
  const [descrizione, setDescrizione] = useState('')
  const [stato, setStato] = useState('')
  const [ubicazione, setUbicazione] = useState('')
  const mut = useMutation({
    mutationFn: () => kitApi.create({ nome, descrizione, stato: stato || undefined, ubicazione: ubicazione || undefined } as any),
    onSuccess,
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore creazione'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Nuovo Kit</h2>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label">Nome Kit *</label>
            <input className="input" placeholder="es. kit1, kit_connettori..." value={nome} onChange={e => setNome(e.target.value)} />
          </div>
          <div>
            <label className="label">Descrizione</label>
            <input className="input" placeholder="Descrizione opzionale..." value={descrizione} onChange={e => setDescrizione(e.target.value)} />
          </div>
          <div>
            <label className="label">Stato</label>
            <select className="input" value={stato} onChange={e => setStato(e.target.value)}>
              <option value="">— Non specificato —</option>
              {KIT_STATI.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Ubicazione</label>
            <input className="input" placeholder="es. Scaffale A3, Furgone AB123CD..." value={ubicazione} onChange={e => setUbicazione(e.target.value)} />
          </div>
        </div>
        <div className="p-6 pt-0 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={() => mut.mutate()} disabled={!nome || mut.isPending} className="btn-primary">Crea Kit</button>
        </div>
      </div>
    </div>
  )
}

function KitEditModal({ kit, onClose, onSuccess }: { kit: any; onClose: () => void; onSuccess: () => void }) {
  const [nome, setNome] = useState(kit.nome)
  const [descrizione, setDescrizione] = useState(kit.descrizione || '')
  const [attivo, setAttivo] = useState(kit.attivo)
  const [stato, setStato] = useState(kit.stato || '')
  const [ubicazione, setUbicazione] = useState(kit.ubicazione || '')
  const mut = useMutation({
    mutationFn: () => kitApi.update(kit.id, { nome, descrizione, attivo, stato: stato || null, ubicazione: ubicazione || null } as any),
    onSuccess: () => { onSuccess(); toast.success('Kit aggiornato') },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore aggiornamento'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Modifica Kit</h2>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label">Nome</label>
            <input className="input" value={nome} onChange={e => setNome(e.target.value)} />
          </div>
          <div>
            <label className="label">Descrizione</label>
            <input className="input" value={descrizione} onChange={e => setDescrizione(e.target.value)} />
          </div>
          <div>
            <label className="label">Stato</label>
            <select className="input" value={stato} onChange={e => setStato(e.target.value)}>
              <option value="">— Non specificato —</option>
              {KIT_STATI.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Ubicazione</label>
            <input className="input" placeholder="es. Scaffale A3, Furgone AB123CD..." value={ubicazione} onChange={e => setUbicazione(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={attivo} onChange={e => setAttivo(e.target.checked)} />
            <span className="text-sm">Kit attivo</span>
          </label>
        </div>
        <div className="p-6 pt-0 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={() => mut.mutate()} disabled={!nome || mut.isPending} className="btn-primary">Salva</button>
        </div>
      </div>
    </div>
  )
}

function KitMaterialiModal({ kit, canWrite, onClose, onSuccess }: { kit: any; canWrite: boolean; onClose: () => void; onSuccess: () => void }) {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const qc = useQueryClient()
  const [kitData, setKitData] = useState<any>(null)
  const [addMode, setAddMode] = useState<'apparato' | 'articolo' | null>(null)
  const [apparatoSearch, setApparatoSearch] = useState('')
  const [articoloSearch, setArticoloSearch] = useState('')
  const [quantita, setQuantita] = useState(0)

  const { data: apparatiRes } = useQuery({
    queryKey: ['apparati-pick', apparatoSearch],
    queryFn: () => magazzinoApi.apparati({ search: apparatoSearch || undefined, per_page: 30, stato: 'in_magazzino_centrale' }).then(r => r.data),
    enabled: addMode === 'apparato',
  })

  const { data: articoliRes } = useQuery({
    queryKey: ['articoli-pick', articoloSearch],
    queryFn: () => magazzinoApi.articoli({ search: articoloSearch || undefined, per_page: 30 }).then(r => r.data),
    enabled: addMode === 'articolo',
  })

  const loadKit = async () => {
    try {
      const res = await kitApi.get(kit.id)
      setKitData(res.data)
    } catch { toast.error('Errore caricamento kit') }
  }

  useEffect(() => { loadKit() }, [kit.id])

  const addMut = useMutation({
    mutationFn: (data: { apparato_id?: number; articolo_id?: number; quantita: number }) =>
      kitApi.addMateriale(kit.id, data),
    onSuccess: (res) => {
      setKitData(res.data)
      setAddMode(null)
      setApparatoSearch('')
      setArticoloSearch('')
      setQuantita(1)
      onSuccess()
      toast.success('Materiale aggiunto al kit')
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore'),
  })

  const removeMut = useMutation({
    mutationFn: (kmId: number) => kitApi.removeMateriale(kit.id, kmId),
    onSuccess: (res) => { setKitData(res.data); onSuccess(); toast.success('Materiale rimosso') },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore'),
  })

  const materiali: any[] = kitData?.materiali || []

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-semibold">Gestisci Kit</h2>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-mono">{kit.nome}</span>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-6 space-y-4">
          {/* Materiali lista */}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Materiali nel Kit ({materiali.length})</p>
            {materiali.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm bg-slate-50 rounded-lg">Kit vuoto</div>
            ) : (
              <div className="space-y-2">
                {materiali.map((m: any) => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded mr-2">{m.tipo}</span>
                      <span className="font-medium text-sm">{m.nome}</span>
                      {m.seriale && <span className="ml-2 font-mono text-xs text-blue-600">{m.seriale}</span>}
                      {m.codice && <span className="ml-2 font-mono text-xs text-slate-400">{m.codice}</span>}
                      {m.categoria && <span className="ml-2 text-xs text-slate-400 capitalize">{m.categoria}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">×{m.quantita}</span>
                      {canWrite && (
                        <button
                          onClick={() => confirmDelete(`Rimuovere ${m.nome} dal kit?`, () => removeMut.mutate(m.id))}
                          className="btn-ghost p-1 text-red-400"
                          disabled={removeMut.isPending}
                        ><Trash2 size={12} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Aggiungi materiale */}
          {canWrite && !addMode && (
            <div className="flex gap-2">
              <button onClick={() => setAddMode('apparato')} className="btn-secondary flex-1 text-sm flex items-center justify-center gap-1.5">
                <Plus size={13} /> Aggiungi Apparato
              </button>
              <button onClick={() => setAddMode('articolo')} className="btn-secondary flex-1 text-sm flex items-center justify-center gap-1.5">
                <Plus size={13} /> Aggiungi Articolo
              </button>
            </div>
          )}

          {addMode === 'apparato' && (
            <div className="border rounded-lg p-4 space-y-3 bg-blue-50">
              <p className="text-sm font-medium text-blue-700">Aggiungi Apparato al Kit</p>
              <input
                className="input"
                placeholder="Cerca per seriale o modello..."
                value={apparatoSearch}
                onChange={e => setApparatoSearch(e.target.value)}
                autoFocus
              />
              {(apparatiRes?.items || []).length > 0 && (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(apparatiRes?.items || []).map((a: any) => (
                    <div
                      key={a.id}
                      className="p-2 bg-white rounded cursor-pointer hover:bg-blue-50 flex items-center justify-between text-sm"
                      onClick={() => addMut.mutate({ apparato_id: a.id, quantita: 1 })}
                    >
                      <span>
                        <span className="font-mono text-blue-700">{a.seriale}</span>
                        {a.modello && <span className="ml-2 text-slate-500">{a.modello}</span>}
                      </span>
                      <span className="text-xs text-slate-400 capitalize">{a.categoria}</span>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setAddMode(null)} className="btn-secondary w-full text-sm">Annulla</button>
            </div>
          )}

          {addMode === 'articolo' && (
            <div className="border rounded-lg p-4 space-y-3 bg-amber-50">
              <p className="text-sm font-medium text-amber-700">Aggiungi Articolo al Kit</p>
              <input
                className="input"
                placeholder="Cerca per codice o descrizione..."
                value={articoloSearch}
                onChange={e => setArticoloSearch(e.target.value)}
                autoFocus
              />
              <div>
                <label className="label text-xs">Quantità</label>
                <input type="number" min={1} className="input w-24" value={quantita || ''} placeholder="Qtà" onChange={e => setQuantita(Number(e.target.value))} />
              </div>
              {(articoliRes?.items || []).length > 0 && (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(articoliRes?.items || []).map((a: any) => (
                    <div
                      key={a.id}
                      className="p-2 bg-white rounded cursor-pointer hover:bg-amber-50 flex items-center justify-between text-sm"
                      onClick={() => addMut.mutate({ articolo_id: a.id, quantita })}
                    >
                      <span>
                        <span className="font-mono text-amber-700">{a.codice}</span>
                        <span className="ml-2 text-slate-600">{a.descrizione}</span>
                      </span>
                      <span className="text-xs text-slate-400">Disp: {a.giacenza_centrale}</span>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setAddMode(null)} className="btn-secondary w-full text-sm">Annulla</button>
            </div>
          )}
        </div>
        <div className="p-6 pt-0 flex justify-end">
          <button onClick={onClose} className="btn-secondary">Chiudi</button>
        </div>
      </div>
      {ConfirmModal}
    </div>
  )
}

function SostituisciFurgoneModal({ furgone, onClose, onSuccess }: { furgone: any; onClose: () => void; onSuccess: () => void }) {
  const [nuovaTarga, setNuovaTarga] = useState('')
  const [spostaContenuto, setSpostaContenuto] = useState(true)
  const mut = useMutation({
    mutationFn: () => magazzinoApi2.sostituisciFurgone(furgone.targa, nuovaTarga.trim().toUpperCase(), spostaContenuto),
    onSuccess: (res) => {
      const d = res.data
      toast.success(`Furgone ${d.targa} → ${d.nuova_targa}` + (spostaContenuto ? ` (spostati ${d.apparati} apparati, ${d.articoli} materiali)` : ' — contenuto rientrato in centrale'))
      onSuccess()
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore sostituzione furgone'),
  })
  const haTecnico = furgone.tecnico && furgone.tecnico !== furgone.targa
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Sostituisci Furgone</h2>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm bg-amber-50 p-3 rounded-lg">
            <div><span className="text-slate-500">Furgone attuale:</span> <span className="font-mono font-bold">{furgone.targa}</span></div>
            <div><span className="text-slate-500">Tecnico:</span> {haTecnico ? furgone.tecnico : <em className="text-slate-400">nessuno</em>}</div>
            <div className="text-xs text-slate-500 mt-1">{furgone.apparati.length} apparati · {furgone.articoli.length} materiali</div>
          </div>
          <div>
            <label className="label">Nuova targa *</label>
            <input
              className="input font-mono uppercase"
              value={nuovaTarga}
              onChange={e => setNuovaTarga(e.target.value.toUpperCase())}
              placeholder="es. AB123CD"
              autoFocus
            />
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={spostaContenuto} onChange={e => setSpostaContenuto(e.target.checked)} />
            <span>
              Sposta il contenuto sul nuovo furgone
              <span className="block text-xs text-slate-400">Se disattivato, apparati, materiali e kit rientrano in Magazzino Centrale</span>
            </span>
          </label>
        </div>
        <div className="p-6 pt-0 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button
            onClick={() => mut.mutate()}
            disabled={!nuovaTarga.trim() || mut.isPending}
            className="btn-primary flex items-center gap-1.5"
          >
            <ArrowRightLeft size={14} /> {mut.isPending ? 'Sostituzione...' : 'Sostituisci'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Tutte le destinazioni furgone: tecnici attivi con targa + furgoni che hanno ancora contenuto
function useDestinazioniFurgoni() {
  const { data, isLoading } = useQuery({
    queryKey: ['magazzino-furgoni-list'],
    queryFn: () => magazzinoApi2.furgoniList().then(r => r.data as any[]),
    staleTime: 0,
  })
  const furgoni: { targa: string; label: string }[] = (data ?? [])
    .filter((f: any) => f.targa)
    .map((f: any) => ({
      targa: f.targa,
      label: f.tecnico && f.tecnico !== f.targa ? `Furgone ${f.targa} — ${f.tecnico}` : `Furgone ${f.targa} (non assegnato)`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
  return { furgoni, isLoading }
}

function KitTransferModal({ kit, onClose, onSuccess }: { kit: any; onClose: () => void; onSuccess: () => void }) {
  const [destinazione, setDestinazione] = useState('')

  const { furgoni, isLoading: loadingTecnici } = useDestinazioniFurgoni()

  const mut = useMutation({
    mutationFn: () => kitApi.transfer(kit.id, { da_ubicazione: 'Magazzino centrale', a_ubicazione: destinazione }),
    onSuccess,
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore trasferimento'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Trasferisci Kit</h2>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm bg-slate-50 p-3 rounded-lg">
            <span className="text-slate-500">Kit:</span>
            <span className="ml-2 font-mono font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{kit.nome}</span>
            <div className="text-xs text-slate-400 mt-1">{kit.materiali_count} materiali inclusi</div>
          </div>
          <div>
            <label className="label">Destinazione *</label>
            {loadingTecnici ? (
              <div className="input text-slate-400 text-sm">Caricamento furgoni...</div>
            ) : (
              <select className="select" value={destinazione} onChange={e => setDestinazione(e.target.value)}>
                <option value="">— Seleziona destinazione —</option>
                <option value="Magazzino centrale">Magazzino Centrale</option>
                {furgoni.map(f => (
                  <option key={f.targa} value={f.targa}>{f.label}</option>
                ))}
              </select>
            )}
            {!loadingTecnici && furgoni.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">Nessun furgone configurato (tecnici senza targa)</p>
            )}
          </div>
        </div>
        <div className="p-6 pt-0 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={() => mut.mutate()} disabled={!destinazione || mut.isPending} className="btn-primary flex items-center gap-1.5">
            <ArrowRightLeft size={14} /> Trasferisci
          </button>
        </div>
      </div>
    </div>
  )
}

function SpostaApparatoModal({ apparato, onClose, onSuccess }: { apparato: Apparato; onClose: () => void; onSuccess: () => void }) {
  const { data: furgoniData } = useQuery({
    queryKey: ['magazzino-furgoni-list'],
    queryFn: () => magazzinoApi2.furgoniList().then(r => r.data as any[]),
  })
  const furgoni: any[] = furgoniData ?? []
  const targaAttuale = apparato.ubicazione?.descrizione || ''
  const [destinazione, setDestinazione] = useState(targaAttuale ? 'centrale' : furgoni[0]?.targa || 'centrale')

  const mutation = useMutation({
    mutationFn: () => magazzinoApi.sposta({
      tipo: 'apparato',
      id: apparato.id,
      sorgente: targaAttuale || 'centrale',
      destinazione,
    }),
    onSuccess,
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Errore spostamento'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Sposta Apparato</h2>
            <p className="text-xs text-slate-400 font-mono mt-0.5">{apparato.seriale}</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
            <div className="font-medium">{apparato.modello || '—'}</div>
            <div className="text-xs text-slate-400">
              Attualmente: {targaAttuale ? `Furgone ${targaAttuale}` : 'Magazzino Centrale'}
            </div>
          </div>
          <div>
            <label className="label">Destinazione</label>
            <select className="select" value={destinazione} onChange={e => setDestinazione(e.target.value)}>
              {targaAttuale && <option value="centrale">↩ Magazzino Centrale</option>}
              {furgoni.filter(f => f.targa !== targaAttuale).map(f => (
                <option key={f.targa} value={f.targa}>🚐 Furgone {f.targa} — {f.tecnico}</option>
              ))}
              {!targaAttuale && furgoni.map(f => (
                <option key={f.targa} value={f.targa}>🚐 Furgone {f.targa} — {f.tecnico}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !destinazione}
              className="btn-primary flex-1"
            >
              {mutation.isPending ? 'Spostamento...' : 'Conferma spostamento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CreateApparatoModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    seriale: '', modello: '', marca: '', categoria: 'altro',
    descrizione: '', fornitore: '', in_garanzia: true,
  })
  const f = (k: string, v: string | boolean) => setForm(p => ({ ...p, [k]: v }))

  const mutation = useMutation({
    mutationFn: (data: typeof form) => magazzinoApi.createApparato(data),
    onSuccess,
    onError: () => toast.error('Errore (seriale già esistente?)'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-lg">
        <div className="p-6 border-b"><h2 className="text-lg font-semibold">Inserisci Apparato</h2></div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form) }} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><label className="label">Numero Seriale *</label><input className="input font-mono uppercase" value={form.seriale} onChange={(e) => f('seriale', e.target.value.toUpperCase())} required /></div>
            <div>
              <label className="label">Categoria</label>
              <select className="select" value={form.categoria} onChange={(e) => f('categoria', e.target.value)}>
                {['ont','cpe','router','antenna_wifi','cavo_fibra','connettore','patch_cord','consumabile','altro'].map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div><label className="label">Marca</label><input className="input" value={form.marca} onChange={(e) => f('marca', e.target.value)} /></div>
            <div className="col-span-2"><label className="label">Modello</label><input className="input" value={form.modello} onChange={(e) => f('modello', e.target.value)} /></div>
            <div><label className="label">Fornitore</label><input className="input" value={form.fornitore} onChange={(e) => f('fornitore', e.target.value)} /></div>
            <div className="flex items-center gap-2 mt-6">
              <input type="checkbox" id="garanzia" checked={form.in_garanzia} onChange={(e) => f('in_garanzia', e.target.checked)} />
              <label htmlFor="garanzia" className="text-sm text-slate-700 cursor-pointer">In garanzia</label>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary flex-1">
              {mutation.isPending ? 'Caricamento...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ApparatoDetailModal({ id, onClose, onSuccess }: { id: number; onClose: () => void; onSuccess: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['apparato', id],
    queryFn: () => magazzinoApi.getApparato(id).then(r => r.data),
  })
  const [form, setForm] = useState<Record<string, string>>({})
  const [dirty, setDirty] = useState(false)

  if (data && !dirty && Object.keys(form).length === 0) {
    setForm({
      seriale: data.seriale || '',
      modello: data.modello || '',
      marca: data.marca || '',
      categoria: data.categoria || '',
      stato: data.stato || '',
      note: data.note || '',
    })
  }

  const f = (k: string, v: string) => { setForm(p => ({ ...p, [k]: v })); setDirty(true) }

  const mutation = useMutation({
    mutationFn: () => magazzinoApi.updateApparato(id, form),
    onSuccess,
    onError: () => toast.error('Errore salvataggio'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-lg">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Dettaglio Apparato</h2>
            {data && <p className="text-xs text-slate-400 mt-0.5">ID #{data.id}</p>}
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 text-slate-400">✕</button>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-slate-400">Caricamento...</div>
        ) : data ? (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="label">Numero Seriale</label>
                <input className="input font-mono uppercase" value={form.seriale || ''} onChange={e => f('seriale', e.target.value.toUpperCase())} />
              </div>
              <div>
                <label className="label">Categoria</label>
                <select className="select" value={form.categoria || ''} onChange={e => f('categoria', e.target.value)}>
                  {['ont','cpe','router','antenna_wifi','cavo_fibra','connettore','patch_cord','consumabile','altro'].map(c =>
                    <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="label">Stato</label>
                <select className="select" value={form.stato || ''} onChange={e => f('stato', e.target.value)}>
                  {Object.entries(STATI_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Marca</label>
                <input className="input" value={form.marca || ''} onChange={e => f('marca', e.target.value)} />
              </div>
              <div>
                <label className="label">Modello</label>
                <input className="input" value={form.modello || ''} onChange={e => f('modello', e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className="label">Note</label>
                <textarea className="input" rows={2} value={form.note || ''} onChange={e => f('note', e.target.value)} />
              </div>
            </div>
            {data.committente && (
              <div className="text-sm text-slate-500">Committente: <span className="font-medium text-slate-700">{data.committente}</span></div>
            )}
            {data.stato_display && (
              <div className="text-sm text-slate-500">Posizione: <span className="font-medium text-slate-700">{data.stato_display}</span></div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
              <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !dirty} className="btn-primary flex-1">
                {mutation.isPending ? 'Salvando...' : 'Salva modifiche'}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-slate-400">Apparato non trovato</div>
        )}
      </div>
    </div>
  )
}

function AssegnaModal({ apparato, onClose, onSuccess }: { apparato: Apparato; onClose: () => void; onSuccess: () => void }) {
  const [tipo, setTipo] = useState('magazzino')
  const [targa, setTarga] = useState('')
  const [descrizione, setDescrizione] = useState('')

  const mutation = useMutation({
    mutationFn: () => magazzinoApi.assegna(apparato.id, { tipo, targa: tipo === 'furgone' ? targa : undefined, descrizione }),
    onSuccess,
    onError: () => toast.error('Errore assegnazione'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Assegna Apparato</h2>
          <p className="text-sm text-slate-500 font-mono mt-0.5">{apparato.seriale}</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label">Destinazione</label>
            <select className="select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="magazzino">Magazzino Centrale</option>
              <option value="furgone">Furgone</option>
              <option value="installato">Installato</option>
            </select>
          </div>
          {tipo === 'furgone' && (
            <div>
              <label className="label">Targa Furgone</label>
              <input className="input uppercase" value={targa} onChange={(e) => setTarga(e.target.value.toUpperCase())} placeholder="es. AB123CD" />
            </div>
          )}
          {tipo === 'installato' && (
            <div>
              <label className="label">Indirizzo / Sito</label>
              <input className="input" value={descrizione} onChange={(e) => setDescrizione(e.target.value)} placeholder="Indirizzo di installazione" />
            </div>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button onClick={() => mutation.mutate()} disabled={mutation.isPending} className="btn-primary flex-1">
              {mutation.isPending ? 'Salvando...' : 'Assegna'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ArticoloModal({ articolo, onClose, onSuccess }: { articolo: any | null; onClose: () => void; onSuccess: () => void }) {
  const isEdit = !!articolo
  const [form, setForm] = useState({
    codice: articolo?.codice || '',
    descrizione: articolo?.descrizione || '',
    categoria: articolo?.categoria || '',
    unita_misura: articolo?.unita_misura || 'pz',
    giacenza_centrale: articolo?.giacenza_centrale ?? 0,
    soglia_minima_riordino: articolo?.soglia_minima_riordino ?? 0,
    prezzo_unitario: articolo?.prezzo_unitario ?? '',
    fornitore: articolo?.fornitore || '',
    note: articolo?.note || '',
  })
  const f = (k: string, v: string | number) => setForm(p => ({ ...p, [k]: v }))

  const mutation = useMutation({
    mutationFn: () => isEdit
      ? magazzinoApi.updateArticolo(articolo.id, form)
      : magazzinoApi.createArticolo(form),
    onSuccess,
    onError: () => toast.error('Errore salvataggio (codice già esistente?)'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-lg">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? 'Modifica Articolo' : 'Nuovo Articolo'}</h2>
          <button onClick={onClose} className="btn-ghost p-1.5 text-slate-400">✕</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate() }} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Codice *</label>
              <input className="input font-mono uppercase" value={form.codice} onChange={e => f('codice', e.target.value)} required />
            </div>
            <div>
              <label className="label">U.M.</label>
              <select className="select" value={form.unita_misura} onChange={e => f('unita_misura', e.target.value)}>
                {['pz', 'm', 'mt', 'kg', 'l', 'conf', 'rotolo', 'box'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="label">Descrizione *</label>
              <input className="input" value={form.descrizione} onChange={e => f('descrizione', e.target.value)} required />
            </div>
            <div>
              <label className="label">Categoria</label>
              <input className="input" value={form.categoria} onChange={e => f('categoria', e.target.value)} placeholder="es. cavi, connettori..." />
            </div>
            <div>
              <label className="label">Fornitore</label>
              <input className="input" value={form.fornitore} onChange={e => f('fornitore', e.target.value)} />
            </div>
            <div>
              <label className="label">Giacenza Centrale</label>
              <input type="number" min={0} step={0.01} className="input" value={form.giacenza_centrale} onChange={e => f('giacenza_centrale', Number(e.target.value))} />
            </div>
            <div>
              <label className="label">Soglia Minima Riordino</label>
              <input type="number" min={0} step={0.01} className="input" value={form.soglia_minima_riordino} onChange={e => f('soglia_minima_riordino', Number(e.target.value))} />
            </div>
            <div>
              <label className="label">Prezzo Unitario €</label>
              <input type="number" min={0} step={0.01} className="input" value={form.prezzo_unitario} onChange={e => f('prezzo_unitario', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="label">Note</label>
              <textarea className="input" rows={2} value={form.note} onChange={e => f('note', e.target.value)} />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary flex-1">
              {mutation.isPending ? 'Salvando...' : isEdit ? 'Salva modifiche' : 'Crea articolo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


function SpostaApparatoBtn({ apparatoId, targa, onSuccess }: { apparatoId: number; targa: string; onSuccess: () => void }) {
  const mutation = useMutation({
    mutationFn: () => magazzinoApi.sposta({ tipo: 'apparato', id: apparatoId, destinazione: 'centrale' }),
    onSuccess,
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Errore spostamento'),
  })
  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="btn-ghost px-2 py-1 text-xs text-slate-500 hover:text-blue-600"
      title="Restituisci al magazzino"
    >
      {mutation.isPending ? '...' : '↩ Magazzino'}
    </button>
  )
}

function SpostaArticoloModal({ articolo, onClose, onSuccess }: { articolo: any; onClose: () => void; onSuccess: () => void }) {
  const { data: furgoniData } = useQuery({
    queryKey: ['magazzino-furgoni-list'],
    queryFn: () => magazzinoApi2.furgoniList().then(r => r.data as any[]),
  })
  const furgoni: any[] = furgoniData ?? []
  const [sorgente, setSorgente] = useState('centrale')
  const [destinazione, setDestinazioneState] = useState('centrale')
  const [quantita, setQuantita] = useState(0)

  const mutation = useMutation({
    mutationFn: () => magazzinoApi.sposta({ tipo: 'articolo', id: articolo.id, sorgente, destinazione, quantita }),
    onSuccess,
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Giacenza insufficiente'),
  })

  const giacenzaSorgente = sorgente === 'centrale'
    ? (articolo.giacenza_centrale ?? 0)
    : (furgoni.find(f => f.targa === sorgente)?.articoli.find((a: any) => a.codice === articolo.codice)?.quantita ?? 0)

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Trasferisci Materiale</h2>
            <p className="text-xs text-slate-400 font-mono mt-0.5">{articolo.codice} — {articolo.descrizione}</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label">Da (sorgente)</label>
            <select className="select" value={sorgente} onChange={e => setSorgente(e.target.value)}>
              <option value="centrale">Magazzino Centrale ({articolo.giacenza_centrale ?? 0} {articolo.unita_misura})</option>
              {furgoni.map(f => {
                const qty = f.articoli.find((a: any) => a.codice === articolo.codice)?.quantita ?? 0
                return qty > 0 ? <option key={f.targa} value={f.targa}>Furgone {f.targa} — {f.tecnico} ({qty} {articolo.unita_misura})</option> : null
              })}
            </select>
          </div>
          <div>
            <label className="label">A (destinazione)</label>
            <select className="select" value={destinazione} onChange={e => setDestinazioneState(e.target.value)}>
              <option value="centrale">Magazzino Centrale</option>
              {furgoni.map(f => (
                <option key={f.targa} value={f.targa}>Furgone {f.targa} — {f.tecnico}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Quantità (max {giacenzaSorgente})</label>
            <input
              type="number" min={1} max={giacenzaSorgente}
              className="input" value={quantita || ''} placeholder="Qtà"
              onChange={e => setQuantita(Math.min(giacenzaSorgente, Number(e.target.value)))}
            />
          </div>
          {sorgente === destinazione && (
            <p className="text-xs text-amber-600">Sorgente e destinazione devono essere diversi.</p>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || sorgente === destinazione || quantita < 1 || quantita > giacenzaSorgente}
              className="btn-primary flex-1"
            >
              {mutation.isPending ? 'Trasferimento...' : 'Conferma Trasferimento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Modal Trasferimento ───────────────────────────────────────────────────────
function TrasferisciModal({ item, tipo, targaCorrente, onClose, onSuccess }: {
  item: any; tipo: 'apparato' | 'articolo' | 'kit'; targaCorrente: string; onClose: () => void; onSuccess: () => void
}) {
  const [aTarga, setATarga] = useState('')
  const [quantita, setQuantita] = useState(0)
  const [note, setNote] = useState('')
  const qc = useQueryClient()

  const { data: furgoniData } = useQuery({
    queryKey: ['furgoni-list'],
    queryFn: () => magazzinoApi2.furgoni().then(r => r.data),
  })
  const altriFurgoni: Array<{targa: string; tecnico: string}> =
    (furgoniData || []).filter((f: any) => f.targa !== targaCorrente)

  const mut = useMutation({
    mutationFn: () => magazzinoApi2.richiediTrasferimento({
      tipo, item_id: item.id,
      a_targa: aTarga || null,
      quantita, note,
    }),
    onSuccess: () => {
      toast.success('Richiesta inviata — il destinatario deve confermare')
      qc.invalidateQueries({ queryKey: ['trasferimenti-in-arrivo'] })
      onSuccess()
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore invio richiesta'),
  })

  const destinazioni = [
    { label: '↩ Magazzino Centrale', value: '' },
    ...altriFurgoni.map(f => ({ label: `Furgone ${f.targa} — ${f.tecnico}`, value: f.targa })),
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="font-semibold text-slate-800">📦 Richiedi Trasferimento</h3>

        <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
          <div className="font-medium">{tipo === 'kit' ? `Kit ${item.nome}` : (item.modello || item.descrizione)}</div>
          {tipo === 'kit' && item.descrizione && <div className="text-xs text-slate-400">{item.descrizione}</div>}
          {tipo !== 'kit' && item.seriale && <div className="font-mono text-xs text-slate-400">{item.seriale}</div>}
          {tipo === 'kit' && (
            <div className="text-xs text-slate-400 mt-1">{(item.materiali ?? []).length} componenti inclusi</div>
          )}
        </div>

        <div>
          <label className="label">Destinazione *</label>
          <select className="input" value={aTarga} onChange={e => setATarga(e.target.value)}>
            {destinazioni.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>

        {tipo === 'articolo' && (
          <div>
            <label className="label">Quantità</label>
            <input type="number" min={1} className="input" value={quantita || ''} placeholder="Qtà"
              onChange={e => setQuantita(Number(e.target.value))} />
          </div>
        )}
        {tipo === 'kit' && (
          <p className="text-xs text-purple-700 bg-purple-50 rounded p-2">
            📦 Verranno trasferiti il kit e tutti i suoi apparati.
          </p>
        )}

        <div>
          <label className="label">Note (opzionale)</label>
          <input className="input" placeholder="Es. restituzione fine intervento"
            value={note} onChange={e => setNote(e.target.value)} />
        </div>

        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
          ⚠ Il materiale verrà spostato solo dopo la conferma del destinatario.
        </p>

        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          <button onClick={() => mut.mutate()} disabled={mut.isPending || (tipo === 'articolo' && quantita < 1)} className="btn-primary flex-1" title={tipo === 'kit' ? `Trasferisce kit "${item.nome}" con tutti i suoi apparati` : undefined}>
            {mut.isPending ? 'Invio...' : 'Richiedi Trasferimento'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Vista read-only per tecnici: solo il proprio furgone ─────────────────────
function MagazzinoFurgoneTecnico() {
  const qc = useQueryClient()
  const [tabFurgone, setTabFurgone] = useState<'inventario' | 'arrivo'>('inventario')
  const [trasferisciItem, setTrasferisciItem] = useState<{ item: any; tipo: 'apparato' | 'articolo' | 'kit' } | null>(null)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['magazzino-mio-furgone'],
    queryFn: () => magazzinoApi2.daFurgone().then(r => r.data),
    staleTime: 60_000,
  })

  const { data: inArrivo = [] } = useQuery({
    queryKey: ['trasferimenti-in-arrivo'],
    queryFn: () => magazzinoApi2.trasferimentiInArrivo().then(r => r.data),
    refetchInterval: 60_000,
    enabled: !!data?.targa,
  })

  useEffect(() => {
    if ((inArrivo as any[]).length > 0) setTabFurgone('arrivo')
  }, [(inArrivo as any[]).length > 0])

  const confermaMut = useMutation({
    mutationFn: ({ id, azione }: { id: number; azione: 'conferma' | 'rifiuta' }) =>
      magazzinoApi2.confermaTrasferimento(id, azione),
    onSuccess: (_, { azione }) => {
      toast.success(azione === 'conferma' ? 'Trasferimento confermato' : 'Trasferimento rifiutato')
      qc.invalidateQueries({ queryKey: ['trasferimenti-in-arrivo'] })
      qc.invalidateQueries({ queryKey: ['magazzino-mio-furgone'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore'),
  })

  const apparati: any[] = data?.apparati ?? []
  const articoli: any[] = data?.articoli ?? []
  const kitFurgone: any[] = data?.kit ?? []
  const targaCorrente: string = data?.targa || ''

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Il Mio Furgone</h1>
        <p className="text-slate-500 text-sm">
          {targaCorrente ? `Targa: ${targaCorrente}` : 'Nessun veicolo assegnato'}
        </p>
      </div>

      {(inArrivo as any[]).length > 0 && tabFurgone === 'inventario' && (
        <button
          onClick={() => setTabFurgone('arrivo')}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            background: '#fff7ed', border: '1px solid #fed7aa',
            borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
            color: '#92400e', fontSize: 13, fontWeight: 600,
          }}
        >
          <ArrowRightLeft size={16} style={{ color: '#F97316', flexShrink: 0 }} />
          {(inArrivo as any[]).length} trasferiment{(inArrivo as any[]).length === 1 ? 'o' : 'i'} in arrivo da confermare
          <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.7 }}>Vai alla tab →</span>
        </button>
      )}

      {isLoading && (
        <div className="card p-10 text-center text-slate-400">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          Caricamento...
        </div>
      )}

      {!isLoading && (!targaCorrente || data?.message) && (
        <div className="card p-10 text-center text-slate-400">
          <Truck size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">{data?.message ?? 'Nessun veicolo assegnato al tuo profilo.'}</p>
          <p className="text-sm mt-1">Contatta un amministratore per associare un veicolo.</p>
        </div>
      )}

      {!isLoading && targaCorrente && (
        <>
          {/* Tab bar */}
          <div className="flex gap-1 border-b">
            <button
              onClick={() => setTabFurgone('inventario')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tabFurgone === 'inventario' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500'}`}>
              Inventario
            </button>
            <button
              onClick={() => setTabFurgone('arrivo')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${tabFurgone === 'arrivo' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500'}`}>
              Trasferimenti in Arrivo
              {inArrivo.length > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 99, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>
                  {inArrivo.length}
                </span>
              )}
            </button>
          </div>

          {/* ── Tab Inventario ── */}
          {tabFurgone === 'inventario' && (
            <>
              {/* Kit sul furgone */}
              {kitFurgone.length > 0 && (
                <div className="card overflow-hidden">
                  <div className="px-5 py-3 border-b bg-purple-50/60">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Archive size={16} className="text-purple-600" /> Kit
                      <span className="text-xs font-normal text-slate-400">({kitFurgone.length})</span>
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {kitFurgone.map((k: any) => (
                      <div key={k.id} className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-mono font-semibold">{k.nome}</span>
                          {k.descrizione && <span className="text-xs text-slate-500">{k.descrizione}</span>}
                          {!k.attivo && <span className="text-xs text-slate-400 italic">non attivo</span>}
                          <button
                            onClick={() => setTrasferisciItem({ item: k, tipo: 'kit' })}
                            className="ml-auto text-xs px-2.5 py-1 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-1"
                          >
                            <ArrowRightLeft size={12} /> Trasferisci
                          </button>
                        </div>
                        <div className="space-y-1">
                          {k.materiali.map((m: any, i: number) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                              <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{m.tipo}</span>
                              <span className="font-medium">{m.nome}</span>
                              {m.seriale && <span className="font-mono text-xs text-blue-600">{m.seriale}</span>}
                              {m.codice && <span className="font-mono text-xs text-slate-400">{m.codice}</span>}
                              {m.quantita && m.quantita > 1 && <span className="text-xs text-slate-400">×{m.quantita}</span>}
                            </div>
                          ))}
                          {k.materiali.length === 0 && (
                            <p className="text-xs text-slate-400 italic">Kit vuoto</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Apparati sul furgone */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b bg-slate-50/50">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Package size={16} className="text-blue-600" /> Apparati
                    <span className="text-xs font-normal text-slate-400">({apparati.length})</span>
                  </h3>
                </div>
                {apparati.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm">Nessun apparato sul furgone</div>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="text-sm" style={{ minWidth: 500 }}>
                    <thead>
                      <tr className="border-b bg-slate-50/30">
                        <th className="text-left px-4 py-2 font-semibold text-slate-500 text-xs">Seriale</th>
                        <th className="text-left px-4 py-2 font-semibold text-slate-500 text-xs">Modello</th>
                        <th className="text-left px-4 py-2 font-semibold text-slate-500 text-xs">Marca</th>
                        <th className="text-left px-4 py-2 font-semibold text-slate-500 text-xs">Categoria</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-500 text-xs"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {apparati.map((a: any) => (
                        <tr key={a.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-700">{a.seriale}</td>
                          <td className="px-4 py-2.5 text-slate-700">{a.modello || '—'}</td>
                          <td className="px-4 py-2.5 text-slate-500">{a.marca || '—'}</td>
                          <td className="px-4 py-2.5 text-slate-400 capitalize">{a.categoria || '—'}</td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              onClick={() => setTrasferisciItem({ item: a, tipo: 'apparato' })}
                              className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700 transition-colors">
                              <ArrowRightLeft size={12} className="inline mr-1" />Trasferisci
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>

              {/* Materiali di consumo */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b bg-slate-50/50">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Boxes size={16} className="text-amber-600" /> Materiali di consumo
                    <span className="text-xs font-normal text-slate-400">({articoli.length})</span>
                  </h3>
                </div>
                {articoli.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm">Nessun materiale sul furgone</div>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="text-sm" style={{ minWidth: 420 }}>
                    <thead>
                      <tr className="border-b bg-slate-50/30">
                        <th className="text-left px-4 py-2 font-semibold text-slate-500 text-xs">Codice</th>
                        <th className="text-left px-4 py-2 font-semibold text-slate-500 text-xs">Descrizione</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-500 text-xs">Disponibile</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-500 text-xs"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {articoli.map((a: any) => (
                        <tr key={a.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{a.codice}</td>
                          <td className="px-4 py-2.5 text-slate-700">{a.descrizione}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-green-700">
                            {a.quantita_disponibile} {a.unita_misura}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              onClick={() => setTrasferisciItem({ item: a, tipo: 'articolo' })}
                              className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700 transition-colors">
                              <ArrowRightLeft size={12} className="inline mr-1" />Trasferisci
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Tab Trasferimenti in Arrivo ── */}
          {tabFurgone === 'arrivo' && (
            <div className="space-y-3">
              {inArrivo.length === 0 ? (
                <div className="card p-10 text-center text-slate-400 text-sm">
                  Nessun trasferimento in attesa
                </div>
              ) : inArrivo.map((t: any) => (
                <div key={t.id} className="card p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-semibold text-sm text-slate-800">{t.item_nome}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Da: {t.da_tecnico} · Qtà: {t.quantita}
                      {t.note_mittente && ` · Note: ${t.note_mittente}`}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => confermaMut.mutate({ id: t.id, azione: 'rifiuta' })}
                      disabled={confermaMut.isPending}
                      className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                      ✕ Rifiuta
                    </button>
                    <button
                      onClick={() => confermaMut.mutate({ id: t.id, azione: 'conferma' })}
                      disabled={confermaMut.isPending}
                      className="btn-primary text-sm px-3 py-1.5">
                      ✓ Conferma
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal trasferimento */}
      {trasferisciItem && (
        <TrasferisciModal
          item={trasferisciItem.item}
          tipo={trasferisciItem.tipo}
          targaCorrente={targaCorrente}
          onClose={() => setTrasferisciItem(null)}
          onSuccess={() => { setTrasferisciItem(null); refetch() }}
        />
      )}
    </div>
  )
}

// ─── Sposta apparati massivo ─────────────────────────────────────────────────

function BulkMoveApparatiModal({ ids, onClose, onSuccess }: { ids: number[]; onClose: () => void; onSuccess: () => void }) {
  const [destinazione, setDestinazione] = useState('centrale')
  const { data: furgoniData } = useQuery({
    queryKey: ['magazzino-furgoni-list'],
    queryFn: () => magazzinoApi2.furgoniList().then(r => r.data as any[]),
  })
  const furgoni: any[] = furgoniData ?? []

  const mut = useMutation({
    mutationFn: () => magazzinoApi.spostaAppararatiBulk(ids, destinazione),
    onSuccess: (res) => {
      toast.success(`${res.data.spostati} apparati spostati verso "${destinazione === 'centrale' ? 'Magazzino Centrale' : destinazione}"`)
      onSuccess()
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore spostamento'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-sm">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Sposta Apparati</h2>
            <p className="text-xs text-slate-400 mt-0.5">{ids.length} apparati selezionati</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label">Destinazione</label>
            <select className="select" value={destinazione} onChange={e => setDestinazione(e.target.value)}>
              <option value="centrale">Magazzino Centrale</option>
              {furgoni.map(f => (
                <option key={f.targa} value={f.targa}>Furgone {f.targa} — {f.tecnico}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              className="btn-primary flex-1 flex items-center justify-center gap-1.5"
            >
              <ArrowRightLeft size={14} /> {mut.isPending ? 'Spostamento...' : 'Conferma'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Trasferisci kit massivo ─────────────────────────────────────────────────

function BulkTransferKitsModal({ ids, onClose, onSuccess }: { ids: number[]; onClose: () => void; onSuccess: () => void }) {
  const [destinazione, setDestinazione] = useState('Magazzino centrale')
  const { furgoni, isLoading: loadingTecnici } = useDestinazioniFurgoni()

  const mut = useMutation({
    mutationFn: () => kitApi.bulkTransfer(ids, destinazione),
    onSuccess: (res) => {
      toast.success(`${res.data.trasferiti} kit spostati verso "${destinazione}"`)
      onSuccess()
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore trasferimento'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-sm">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Trasferisci Kit</h2>
            <p className="text-xs text-slate-400 mt-0.5">{ids.length} kit selezionati</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label">Destinazione</label>
            {loadingTecnici ? (
              <div className="input text-slate-400 text-sm">Caricamento furgoni...</div>
            ) : (
              <select className="select" value={destinazione} onChange={e => setDestinazione(e.target.value)}>
                <option value="Magazzino centrale">Magazzino Centrale</option>
                {furgoni.map(f => (
                  <option key={f.targa} value={f.targa}>{f.label}</option>
                ))}
              </select>
            )}
            {!loadingTecnici && furgoni.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">Nessun furgone configurato</p>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button
              onClick={() => mut.mutate()}
              disabled={mut.isPending || !destinazione}
              className="btn-primary flex-1 flex items-center justify-center gap-1.5"
            >
              <ArrowRightLeft size={14} /> {mut.isPending ? 'Trasferimento...' : 'Conferma'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const TIPO_LABELS: Record<string, string> = {
  kit_installato_apparati_mismatch:       'Kit installato / Apparati non installati',
  apparati_installati_kit_non_installato: 'Apparati installati / Kit non installato',
  kit_spezzato_su_furgoni_diversi:        'Kit spezzato su furgoni diversi',
  kit_su_furgone_senza_apparati_furgone:  'Kit su furgone senza apparati',
  kit_senza_apparati:                     'Kit senza apparati collegati',
  apparato_installato_wo_non_eseguito:    'Apparato installato / WO non eseguito',
  kit_non_installato_in_rapportino:       'Kit non installato / presente in rapportino',
  kit_wo_diversa_da_rapportino:           'Kit su WO diversa dal rapportino',
  apparato_non_installato_in_rapportino:  'Apparato non installato / presente in rapportino',
  apparato_wo_diversa_da_rapportino:      'Apparato su WO diversa dal rapportino',
  apparato_installato_senza_rapportino:   'Apparato installato / assente nei rapportini',
}

const TIPI_ALLINEA_RAPPORTINO = [
  'kit_non_installato_in_rapportino', 'kit_wo_diversa_da_rapportino',
  'apparato_non_installato_in_rapportino', 'apparato_wo_diversa_da_rapportino',
]

function DiscrepanzeTab() {
  const qc = useQueryClient()
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['discrepanze-magazzino'],
    queryFn: () => magazzinoApi.discrepanze().then(r => r.data),
    staleTime: 60_000,
  })
  const [filtroSev, setFiltroSev] = useState<'all' | 'critica' | 'warning'>('all')
  const [filtroTipo, setFiltroTipo] = useState('')

  const segnaInstalattoMut = useMutation({
    mutationFn: (kitId: number) => kitApi.segnaInstallato(kitId),
    onSuccess: (res) => {
      toast.success(`Kit "${res.data.kit_nome}" segnato come installato su WO ${res.data.wo_numero}`)
      qc.invalidateQueries({ queryKey: ['discrepanze-magazzino'] })
      qc.invalidateQueries({ queryKey: ['kits'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore assegnazione kit'),
  })

  const reimpostaStatoMut = useMutation({
    mutationFn: (kitId: number) => kitApi.reimpostaStato(kitId),
    onSuccess: (res) => {
      toast.success(`Kit "${res.data.kit_nome}": stato reimpostato`)
      qc.invalidateQueries({ queryKey: ['discrepanze-magazzino'] })
      qc.invalidateQueries({ queryKey: ['kits'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore reimposta stato'),
  })

  const allineaStatoMut = useMutation({
    mutationFn: (kitId: number) => kitApi.allineaStato(kitId),
    onSuccess: (res) => {
      toast.success(`Kit "${res.data.kit_nome}": allineato a "${res.data.stato}"${res.data.ubicazione ? ` · ${res.data.ubicazione}` : ''}`)
      qc.invalidateQueries({ queryKey: ['discrepanze-magazzino'] })
      qc.invalidateQueries({ queryKey: ['kits'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore allineamento stato'),
  })

  const segnaWoEseguitoMut = useMutation({
    mutationFn: (woId: number) => workOrdersApi.updateStato(woId, 'eseguito'),
    onSuccess: () => {
      toast.success('Work order segnato come eseguito')
      qc.invalidateQueries({ queryKey: ['discrepanze-magazzino'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore aggiornamento WO'),
  })

  // ── Riconciliazione rapportini ──────────────────────────────────────────
  const [riconciliaAbilitato, setRiconciliaAbilitato] = useState(false)
  const [riconciliaReport, setRiconciliaReport] = useState<any>(null)

  const { data: riconciliaPreview, isFetching: riconciliaLoading, refetch: riconciliaAnalizza } = useQuery({
    queryKey: ['riconcilia-preview'],
    queryFn: () => magazzinoApi.previewRiconcilia().then(r => r.data),
    enabled: riconciliaAbilitato,
    staleTime: 0,
  })

  const eseguiRiconciliaMut = useMutation({
    mutationFn: () => magazzinoApi.eseguiRiconcilia(),
    onSuccess: (res) => {
      const r = res.data
      setRiconciliaReport(r)
      setRiconciliaAbilitato(false)
      toast.success(`Bonificati ${r.bonificati} apparati · ${r.kit_aggiornati} kit aggiornati`)
      qc.invalidateQueries({ queryKey: ['discrepanze-magazzino'] })
      qc.invalidateQueries({ queryKey: ['kits'] })
      qc.invalidateQueries({ queryKey: ['riconcilia-preview'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore bonifica'),
  })

  // ── Riconciliazione materiali rapportini ────────────────────────────────
  const [riconciliaMatAbilitato, setRiconciliaMatAbilitato] = useState(false)
  const [riconciliaMatReport, setRiconciliaMatReport] = useState<any>(null)

  const { data: riconciliaMatPreview, isFetching: riconciliaMatLoading, refetch: riconciliaMatAnalizza } = useQuery({
    queryKey: ['riconcilia-materiali-preview'],
    queryFn: () => magazzinoApi.previewRiconciliaMateriali().then(r => r.data),
    enabled: riconciliaMatAbilitato,
    staleTime: 0,
  })

  const eseguiRiconciliaMatMut = useMutation({
    mutationFn: () => magazzinoApi.eseguiRiconciliaMateriali(),
    onSuccess: (res) => {
      const r = res.data
      setRiconciliaMatReport(r)
      setRiconciliaMatAbilitato(false)
      toast.success(`Bonificati ${r.bonificati} scarichi materiali`)
      qc.invalidateQueries({ queryKey: ['riconcilia-materiali-preview'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore bonifica materiali'),
  })

  const [risolviReport, setRisolviReport] = useState<any>(null)

  const risolviTuttoMut = useMutation({
    mutationFn: () => magazzinoApi.risolviDiscrepanze(),
    onSuccess: (res) => {
      const r = res.data
      setRisolviReport(r)
      const saltate = r.saltate > 0 ? ` · ${r.saltate} saltate` : ''
      const err = r.errori > 0 ? ` · ${r.errori} errori` : ''
      toast.success(`Risolte ${r.risolte} discrepanze${saltate}${err}`)
      qc.invalidateQueries({ queryKey: ['discrepanze-magazzino'] })
      qc.invalidateQueries({ queryKey: ['kits'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore risoluzione massiva'),
  })

  const allineaRapportinoMut = useMutation({
    mutationFn: (d: any) => magazzinoApi.allineaRapportino(
      d.tipo.startsWith('kit_') ? { kit_id: d.kit_id } : { apparato_id: d.apparato_id }),
    onSuccess: (res) => {
      toast.success(res.data.dettaglio?.map((e: any) => e.messaggio).slice(0, 3).join('\n') || 'Allineato al rapportino')
      qc.invalidateQueries({ queryKey: ['discrepanze-magazzino'] })
      qc.invalidateQueries({ queryKey: ['kits'] })
      qc.invalidateQueries({ queryKey: ['magazzino-furgoni'] })
      qc.invalidateQueries({ queryKey: ['apparati'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore allineamento'),
  })

  const anyPending = allineaRapportinoMut.isPending || segnaInstalattoMut.isPending || reimpostaStatoMut.isPending || allineaStatoMut.isPending || segnaWoEseguitoMut.isPending || risolviTuttoMut.isPending

  const discrepanzeFiltrate: any[] = (data?.discrepanze ?? []).filter((d: any) => {
    if (filtroSev !== 'all' && d.severita !== filtroSev) return false
    if (filtroTipo && d.tipo !== filtroTipo) return false
    return true
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-semibold text-slate-800">Verifica Discrepanze</h2>
          <p className="text-sm text-slate-500">Anomalie tra stato kit, apparati, work orders e rapportini</p>
        </div>
        <div className="flex gap-2">
          {data && data.totale > 0 && (
            <button
              className="text-sm px-3 py-1.5 rounded font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              disabled={anyPending}
              onClick={() => {
                const risolvibili = (data?.discrepanze ?? []).filter((d: any) =>
                  d.tipo !== 'kit_spezzato_su_furgoni_diversi'
                ).length
                const manuali = (data?.discrepanze ?? []).filter((d: any) =>
                  d.tipo === 'kit_spezzato_su_furgoni_diversi'
                ).length
                const msg = [
                  `Risoluzione automatica: ${risolvibili} discrepanz${risolvibili === 1 ? 'a' : 'e'} saranno elaborate.`,
                  manuali > 0 ? `${manuali} kit spezzati su furgoni diversi verranno saltati (richiedono intervento manuale).` : '',
                  '\nContinuare?',
                ].filter(Boolean).join('\n')
                if (confirm(msg)) risolviTuttoMut.mutate()
              }}
            >
              {risolviTuttoMut.isPending ? '⏳ Elaborazione...' : '⚡ Risolvi tutto'}
            </button>
          )}
          <button onClick={() => refetch()} className="btn-secondary text-sm flex items-center gap-1.5">
            🔄 Aggiorna
          </button>
        </div>
      </div>

      {data && (
        <div className="flex gap-3 flex-wrap">
          <div className="card px-5 py-3 flex flex-col items-center min-w-24">
            <span className="text-2xl font-bold text-slate-800">{data.totale}</span>
            <span className="text-xs text-slate-500">Totale</span>
          </div>
          <div className="card px-5 py-3 flex flex-col items-center min-w-24" style={{ borderColor: '#fca5a5', background: '#fef2f2' }}>
            <span className="text-2xl font-bold text-red-700">{data.critiche}</span>
            <span className="text-xs text-red-500">Critiche</span>
          </div>
          <div className="card px-5 py-3 flex flex-col items-center min-w-24" style={{ borderColor: '#fcd34d', background: '#fffbeb' }}>
            <span className="text-2xl font-bold text-amber-700">{data.warning}</span>
            <span className="text-xs text-amber-500">Warning</span>
          </div>
        </div>
      )}

      {/* ── Riconciliazione rapportini ── */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="font-semibold text-slate-700 text-sm">🔍 Riconciliazione Rapportini</span>
            <p className="text-xs text-slate-500 mt-0.5">Seriali presenti nei rapportini ancora su furgone o in magazzino</p>
          </div>
          <div className="flex gap-2">
            {!riconciliaAbilitato && !riconciliaReport && (
              <button
                className="btn-secondary text-sm"
                onClick={() => { setRiconciliaAbilitato(true); setRiconciliaReport(null) }}
              >
                🔍 Analizza
              </button>
            )}
            {riconciliaAbilitato && riconciliaPreview && riconciliaPreview.totale > 0 && (
              <button
                className="text-sm px-3 py-1.5 rounded font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={eseguiRiconciliaMut.isPending}
                onClick={() => {
                  if (confirm(`Marcare come "installati" ${riconciliaPreview.totale} apparati trovati nei rapportini?\nQuesta operazione aggiornerà stato, ubicazione e creerà movimenti di magazzino.`))
                    eseguiRiconciliaMut.mutate()
                }}
              >
                {eseguiRiconciliaMut.isPending ? '⏳ Esecuzione...' : `✓ Applica bonifica (${riconciliaPreview.totale})`}
              </button>
            )}
            {(riconciliaAbilitato || riconciliaReport) && (
              <button
                className="btn-secondary text-sm"
                onClick={() => { setRiconciliaAbilitato(false); setRiconciliaReport(null); riconciliaAnalizza() }}
              >
                🔄 Rianalizza
              </button>
            )}
          </div>
        </div>

        {riconciliaLoading && (
          <div className="px-4 py-6 text-center text-slate-400 text-sm">Analisi in corso...</div>
        )}

        {riconciliaReport && (
          <div className="px-4 py-3 bg-green-50 border-b text-sm text-green-700 font-medium">
            ✓ Bonifica completata: {riconciliaReport.bonificati} apparati aggiornati · {riconciliaReport.kit_aggiornati} kit aggiornati
          </div>
        )}

        {riconciliaAbilitato && riconciliaPreview && !riconciliaLoading && (
          riconciliaPreview.totale === 0 ? (
            <div className="px-4 py-6 text-center text-slate-400 text-sm">
              ✅ Nessuna anomalia — tutti i seriali dei rapportini risultano già installati
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="text-xs w-full">
                <thead>
                  <tr className="border-b bg-slate-50/50">
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Seriale</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Modello</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Stato attuale</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Kit</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">WO rapportino</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">WO stato</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Azione</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {riconciliaPreview.apparati.map((ap: any) => (
                    <tr key={ap.apparato_id} className="hover:bg-slate-50/50">
                      <td className="px-4 py-2 font-mono text-slate-700">{ap.seriale}</td>
                      <td className="px-4 py-2 text-slate-500">{ap.modello || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={clsx('badge text-xs',
                          ap.stato_attuale === 'su_furgone' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                        )}>{ap.stato_attuale}</span>
                      </td>
                      <td className="px-4 py-2 text-slate-500">{ap.kit_nome || '—'}</td>
                      <td className="px-4 py-2">
                        <a href={`/pratiche/${ap.wo_id}`} className="text-blue-600 hover:underline">
                          {ap.wo_numero}
                        </a>
                      </td>
                      <td className="px-4 py-2">
                        <span className={clsx('badge text-xs',
                          ap.wo_stato === 'eseguito' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        )}>{ap.wo_stato}</span>
                      </td>
                      <td className="px-4 py-2 text-green-600 font-medium">→ installato</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {!riconciliaAbilitato && !riconciliaReport && (
          <div className="px-4 py-5 text-center text-slate-400 text-sm">
            Clicca "Analizza" per cercare apparati presenti nei rapportini ma non ancora marcati come installati
          </div>
        )}
      </div>

      {/* ── Riconciliazione materiali rapportini ── */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="font-semibold text-slate-700 text-sm">📦 Riconciliazione Materiali</span>
            <p className="text-xs text-slate-500 mt-0.5">Materiali (ant / 001 / 003) nei rapportini non ancora scaricati da furgone o magazzino</p>
          </div>
          <div className="flex gap-2">
            {!riconciliaMatAbilitato && !riconciliaMatReport && (
              <button className="btn-secondary text-sm"
                onClick={() => { setRiconciliaMatAbilitato(true); setRiconciliaMatReport(null) }}>
                🔍 Analizza
              </button>
            )}
            {riconciliaMatAbilitato && riconciliaMatPreview && riconciliaMatPreview.totale_righe > 0 && (
              <button
                className="text-sm px-3 py-1.5 rounded font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={eseguiRiconciliaMatMut.isPending}
                onClick={() => {
                  const pezzi = riconciliaMatPreview.totale_pezzi
                  if (confirm(`Scaricare ${pezzi} pezzi di materiali (ant/001/003) da ${riconciliaMatPreview.totale_righe} righe di rapportini?\nLo scarico avverrà dal furgone del tecnico se disponibile, altrimenti dalla giacenza centrale.`))
                    eseguiRiconciliaMatMut.mutate()
                }}
              >
                {eseguiRiconciliaMatMut.isPending ? '⏳ Esecuzione...' : `✓ Applica bonifica (${riconciliaMatPreview.totale_righe} righe)`}
              </button>
            )}
            {(riconciliaMatAbilitato || riconciliaMatReport) && (
              <button className="btn-secondary text-sm"
                onClick={() => { setRiconciliaMatAbilitato(false); setRiconciliaMatReport(null); riconciliaMatAnalizza() }}>
                🔄 Rianalizza
              </button>
            )}
          </div>
        </div>

        {riconciliaMatLoading && (
          <div className="px-4 py-6 text-center text-slate-400 text-sm">Analisi in corso...</div>
        )}

        {riconciliaMatReport && (
          <div className="px-4 py-3 bg-green-50 border-b text-sm text-green-700 font-medium">
            ✓ Bonifica completata: {riconciliaMatReport.bonificati} scarichi registrati
          </div>
        )}

        {riconciliaMatAbilitato && riconciliaMatPreview && !riconciliaMatLoading && (
          riconciliaMatPreview.totale_righe === 0 ? (
            <div className="px-4 py-6 text-center text-slate-400 text-sm">
              ✅ Nessuna anomalia — tutti i materiali dei rapportini risultano già scaricati
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="text-xs w-full">
                <thead>
                  <tr className="border-b bg-slate-50/50">
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">WO</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Codice</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Descrizione</th>
                    <th className="text-right px-4 py-2 font-semibold text-slate-600">Qtà rapportino</th>
                    <th className="text-right px-4 py-2 font-semibold text-slate-600">Già scaricato</th>
                    <th className="text-right px-4 py-2 font-semibold text-slate-600">Da scaricare</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Targa</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Stock furgone</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Sorgente</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {riconciliaMatPreview.righe.map((r: any, i: number) => (
                    <tr key={i} className={clsx('hover:bg-slate-50/50', r.anomalia_quantita && 'bg-amber-50/40')}>
                      <td className="px-4 py-2">
                        <a href={`/pratiche/${r.wo_id}`} className="text-blue-600 hover:underline font-mono">
                          {r.wo_numero}
                        </a>
                      </td>
                      <td className="px-4 py-2 font-mono font-semibold text-slate-700">{r.codice}</td>
                      <td className="px-4 py-2 text-slate-500">{r.descrizione}</td>
                      <td className="px-4 py-2 text-right font-medium">{r.quantita_rapportino}</td>
                      <td className="px-4 py-2 text-right text-slate-400">{r.gia_scaricato}</td>
                      <td className="px-4 py-2 text-right font-semibold text-blue-700">{r.da_scaricare}</td>
                      <td className="px-4 py-2 font-mono text-slate-500">{r.targa || '—'}</td>
                      <td className="px-4 py-2 text-right">{r.stock_furgone > 0 ? <span className="text-green-600">{r.stock_furgone}</span> : <span className="text-slate-300">0</span>}</td>
                      <td className="px-4 py-2">
                        <span className={clsx('badge text-xs',
                          r.sorgente === 'furgone' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                        )}>
                          {r.sorgente === 'furgone' ? '🚚 furgone' : '🏭 centrale'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {r.anomalia_quantita && (
                          <span className="badge bg-amber-100 text-amber-700 text-xs">
                            ⚠ atteso {r.quantita_attesa}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {!riconciliaMatAbilitato && !riconciliaMatReport && (
          <div className="px-4 py-5 text-center text-slate-400 text-sm">
            Clicca "Analizza" per cercare scarichi di materiali mancanti nei rapportini
          </div>
        )}
      </div>

      {risolviReport && (
        <div className="card p-4 border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-amber-800 text-sm">
              Ultimo risultato: {risolviReport.risolte} risolte · {risolviReport.saltate} saltate · {risolviReport.errori} errori
            </span>
            <button onClick={() => setRisolviReport(null)} className="text-amber-500 hover:text-amber-700 text-xs">✕ Chiudi</button>
          </div>
          <div className="space-y-0.5 max-h-48 overflow-y-auto">
            {risolviReport.dettaglio.map((d: any, i: number) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className={clsx('shrink-0 font-medium',
                  d.esito === 'ok' ? 'text-green-600' : d.esito === 'saltata' ? 'text-slate-500' : 'text-red-600'
                )}>
                  {d.esito === 'ok' ? '✓' : d.esito === 'saltata' ? '↷' : '✗'}
                </span>
                <span className="text-slate-700">
                  <span className="font-medium">{d.kit_nome}</span>
                  {d.messaggio ? ` — ${d.messaggio}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        {(['all', 'critica', 'warning'] as const).map(s => (
          <button key={s} onClick={() => setFiltroSev(s)}
            className={clsx('px-3 py-1.5 rounded text-xs font-medium border transition-colors',
              filtroSev === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
            )}>
            {s === 'all' ? 'Tutte' : s === 'critica' ? '🔴 Critiche' : '🟡 Warning'}
          </button>
        ))}
        <select className="select text-sm ml-2" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">— Tutti i tipi —</option>
          {Object.entries(TIPO_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="card p-10 text-center text-slate-400">Caricamento...</div>
      ) : discrepanzeFiltrate.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="text-3xl mb-2">✅</div>
          <p className="text-slate-500">Nessuna discrepanza{filtroSev !== 'all' || filtroTipo ? ' con i filtri applicati' : ''}</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="text-sm" style={{ minWidth: 700 }}>
              <thead>
                <tr className="border-b bg-slate-50/50">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Severità</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Tipo</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Kit</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Descrizione</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Apparati</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {discrepanzeFiltrate.map((d: any, i: number) => (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <span className={clsx('badge', d.severita === 'critica' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700')}>
                        {d.severita === 'critica' ? '🔴 Critica' : '🟡 Warning'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600" style={{ maxWidth: 160 }}>
                      {TIPO_LABELS[d.tipo] ?? d.tipo}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-700">
                      {d.kit_nome ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600" style={{ maxWidth: 260 }}>{d.descrizione}</td>
                    <td className="px-4 py-3">
                      {d.apparati?.length > 0 ? (
                        <div className="text-xs space-y-0.5">
                          {d.apparati.slice(0, 3).map((ap: any, j: number) => (
                            <div key={j} className="font-mono text-slate-600">
                              {ap.seriale} <span className="text-slate-400">· {ap.stato}</span>
                            </div>
                          ))}
                          {d.apparati.length > 3 && (
                            <div className="text-slate-400">+{d.apparati.length - 3} altri</div>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 items-start">
                        {d.wo_numero && (
                          <a href={`/pratiche/${d.wo_id}`} className="btn-ghost text-xs text-blue-600 px-0">
                            → WO {d.wo_numero}
                          </a>
                        )}
                        {/* Riscontro rapportini: allinea kit/apparato alla WO del rapportino */}
                        {TIPI_ALLINEA_RAPPORTINO.includes(d.tipo) && (d.kit_id || d.apparato_id) && (
                          <button
                            className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 font-medium transition-colors disabled:opacity-50"
                            disabled={anyPending}
                            onClick={() => {
                              const cosa = d.tipo.startsWith('kit_') ? `il kit "${d.kit_nome}" e i suoi apparati` : `l'apparato ${d.apparati?.[0]?.seriale}`
                              if (confirm(`Segnare ${cosa} come installato su WO ${d.wo_numero}, come da rapportino?`))
                                allineaRapportinoMut.mutate(d)
                            }}
                          >
                            ✓ Allinea a rapportino
                          </button>
                        )}
                        {/* Critiche: segna kit come installato derivando la pratica */}
                        {d.kit_id && ['apparati_installati_kit_non_installato', 'kit_installato_apparati_mismatch'].includes(d.tipo) && (
                          <button
                            className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 font-medium transition-colors disabled:opacity-50"
                            disabled={anyPending}
                            onClick={() => {
                              if (confirm(`Segnare kit "${d.kit_nome}" come installato derivando la pratica dagli apparati?`))
                                segnaInstalattoMut.mutate(d.kit_id)
                            }}
                          >
                            ✓ Segna installato
                          </button>
                        )}
                        {/* Warning: kit con stato incoerente → reimposta a vuoto */}
                        {d.kit_id && d.tipo === 'kit_senza_apparati' && (
                          <button
                            className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium transition-colors disabled:opacity-50"
                            disabled={anyPending}
                            onClick={() => {
                              if (confirm(`Reimpostare stato e ubicazione del kit "${d.kit_nome}" a vuoto?`))
                                reimpostaStatoMut.mutate(d.kit_id)
                            }}
                          >
                            ↺ Reimposta stato
                          </button>
                        )}
                        {/* Warning: kit su_furgone ma apparati non su furgone → allinea */}
                        {d.kit_id && d.tipo === 'kit_su_furgone_senza_apparati_furgone' && (
                          <button
                            className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium transition-colors disabled:opacity-50"
                            disabled={anyPending}
                            onClick={() => {
                              if (confirm(`Allineare stato kit "${d.kit_nome}" a quello degli apparati collegati?`))
                                allineaStatoMut.mutate(d.kit_id)
                            }}
                          >
                            ⇄ Allinea a apparati
                          </button>
                        )}
                        {/* Warning: apparato installato su WO non eseguito → segna WO */}
                        {d.wo_id && d.tipo === 'apparato_installato_wo_non_eseguito' && (
                          <button
                            className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 font-medium transition-colors disabled:opacity-50"
                            disabled={anyPending}
                            onClick={() => {
                              if (confirm(`Segnare WO "${d.wo_numero}" come eseguito?`))
                                segnaWoEseguitoMut.mutate(d.wo_id)
                            }}
                          >
                            ✓ Segna WO eseguito
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── ValorizzazioneBar ────────────────────────────────────────────────────────
function ValorizzazioneBar() {
  const { data } = useQuery({ queryKey: ['valorizzazione'], queryFn: () => valorizzazioneApi.get().then(r => r.data), staleTime: 60_000 })
  if (!data) return null
  const fmt = (v: number) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
  return (
    <div className="flex flex-wrap gap-3 mb-2">
      {[
        { label: 'Valore totale', value: fmt(data.valore_totale ?? 0), color: '#1E40AF' },
        { label: 'Magazzino centrale', value: fmt(data.valore_centrale ?? 0), color: '#15803d' },
        { label: 'Furgoni', value: fmt(data.valore_furgoni ?? 0), color: '#0d9488' },
        { label: 'Articoli attivi', value: String(data.n_articoli ?? 0), color: '#7c3aed' },
        { label: 'Sotto soglia', value: String(data.sotto_soglia ?? 0), color: data.sotto_soglia > 0 ? '#dc2626' : '#15803d' },
        { label: 'In scadenza', value: String(data.in_scadenza ?? 0), color: data.in_scadenza > 0 ? '#f97316' : '#15803d' },
      ].map(k => (
        <div key={k.label} className="card flex flex-col items-center px-4 py-2 min-w-[110px]" style={{ borderTop: `3px solid ${k.color}` }}>
          <span className="text-xs text-slate-500">{k.label}</span>
          <span className="text-base font-bold" style={{ color: k.color }}>{k.value}</span>
        </div>
      ))}
    </div>
  )
}

// ─── TabInventario ────────────────────────────────────────────────────────────
function TabInventario() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = ['superadmin', 'admin'].includes(user?.role ?? '')
  const [selSid, setSelSid] = useState<number | null>(null)
  const [nuovaData, setNuovaData] = useState(new Date().toISOString().slice(0, 10))
  const [search, setSearch] = useState('')

  const { data: sessioni = [] } = useQuery({ queryKey: ['sessioni-inventario'], queryFn: () => inventarioApi.listSessioni().then(r => r.data) })
  const { data: righe = [], isLoading: loadingRighe } = useQuery({
    queryKey: ['inventario-righe', selSid], enabled: selSid != null,
    queryFn: () => inventarioApi.righe(selSid!).then(r => r.data),
  })

  const creaM = useMutation({
    mutationFn: () => inventarioApi.apriSessione({ data: nuovaData }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sessioni-inventario'] }); toast.success('Sessione aperta') },
    onError: () => toast.error('Errore creazione sessione'),
  })
  const chiudiM = useMutation({
    mutationFn: (sid: number) => inventarioApi.chiudiSessione(sid),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['sessioni-inventario'] }); qc.invalidateQueries({ queryKey: ['inventario-righe', selSid] })
      toast.success(`Sessione chiusa — ${r.data.rettifiche_applicate} rettifiche applicate`)
    },
    onError: () => toast.error('Errore chiusura sessione'),
  })
  const eliminaM = useMutation({
    mutationFn: (sid: number) => inventarioApi.eliminaSessione(sid),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sessioni-inventario'] }); setSelSid(null); toast.success('Sessione eliminata') },
    onError: () => toast.error('Errore eliminazione'),
  })
  const aggiornaRigaM = useMutation({
    mutationFn: ({ rid, val }: { rid: number; val: number }) => inventarioApi.aggiornaRiga(selSid!, rid, { conteggio_fisico: val }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventario-righe', selSid] }),
  })
  const approvaRigaM = useMutation({
    mutationFn: ({ rid, val }: { rid: number; val: boolean }) => inventarioApi.aggiornaRiga(selSid!, rid, { approvato: val }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventario-righe', selSid] }),
  })

  const sel = (sessioni as any[]).find(s => s.id === selSid)
  const filteredRighe = (righe as any[]).filter(r => !search || r.codice?.toLowerCase().includes(search.toLowerCase()) || r.descrizione?.toLowerCase().includes(search.toLowerCase()))

  return (
    <div className="space-y-4">
      {ConfirmModal}
      {/* Pannello nuova sessione */}
      {isAdmin && (
        <div className="card flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-slate-700">Nuova sessione inventario:</span>
          <input type="date" className="input" style={{ width: 160 }} value={nuovaData} onChange={e => setNuovaData(e.target.value)} />
          <button className="btn-primary text-sm" onClick={() => creaM.mutate()} disabled={creaM.isPending}>+ Apri sessione</button>
        </div>
      )}

      {/* Lista sessioni */}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200">
            <th className="text-left py-2 px-3 font-medium text-slate-600">Data</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Stato</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Note</th>
            <th className="py-2 px-3"></th>
          </tr></thead>
          <tbody>
            {(sessioni as any[]).map(s => (
              <tr key={s.id} className={clsx('border-b border-slate-100 hover:bg-slate-50 cursor-pointer', selSid === s.id && 'bg-blue-50')} onClick={() => setSelSid(selSid === s.id ? null : s.id)}>
                <td className="py-2 px-3">{s.data}</td>
                <td className="py-2 px-3">
                  <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', s.stato === 'chiusa' ? 'bg-slate-100 text-slate-600' : 'bg-green-100 text-green-700')}>{s.stato}</span>
                </td>
                <td className="py-2 px-3 text-slate-500">{s.note}</td>
                <td className="py-2 px-3 flex gap-2 justify-end" onClick={e => e.stopPropagation()}>
                  {isAdmin && s.stato !== 'chiusa' && (
                    <>
                      <button className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200" onClick={() => { if (confirm('Chiudere la sessione? Le rettifiche approvate verranno applicate.')) chiudiM.mutate(s.id) }}>Chiudi</button>
                      <button className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200" onClick={() => confirmDelete('Eliminare la sessione?', () => eliminaM.mutate(s.id))}>Elimina</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {(sessioni as any[]).length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">Nessuna sessione inventario</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Righe sessione selezionata */}
      {selSid != null && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Sessione #{selSid} — {sel?.data} <span className="text-sm font-normal text-slate-500">({(righe as any[]).length} articoli)</span></h3>
            <input className="input" style={{ width: 220 }} placeholder="Cerca articolo…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {loadingRighe ? <div className="text-center py-4 text-slate-400">Caricamento…</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Codice</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Descrizione</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Attesa</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Fisico</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Delta</th>
                  <th className="py-2 px-2 font-medium text-slate-600">Approvato</th>
                </tr></thead>
                <tbody>
                  {filteredRighe.map((r: any) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-1 px-2 font-mono text-xs">{r.codice}</td>
                      <td className="py-1 px-2">{r.descrizione}</td>
                      <td className="py-1 px-2 text-right">{r.giacenza_attesa} {r.unita_misura}</td>
                      <td className="py-1 px-2 text-right">
                        {sel?.stato !== 'chiusa' ? (
                          <input type="number" min={0} step={0.1} defaultValue={r.conteggio_fisico ?? ''} className="input text-right" style={{ width: 80 }}
                            onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) aggiornaRigaM.mutate({ rid: r.id, val: v }) }} />
                        ) : <span>{r.conteggio_fisico ?? '—'} {r.unita_misura}</span>}
                      </td>
                      <td className="py-1 px-2 text-right font-medium">
                        {r.delta != null ? (
                          <span className={clsx('px-1.5 py-0.5 rounded text-xs', r.delta > 0 ? 'bg-green-100 text-green-700' : r.delta < 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600')}>
                            {r.delta > 0 ? '+' : ''}{r.delta}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-1 px-2 text-center">
                        {sel?.stato !== 'chiusa' ? (
                          <input type="checkbox" checked={r.approvato ?? false} onChange={e => approvaRigaM.mutate({ rid: r.id, val: e.target.checked })} />
                        ) : (r.approvato ? '✅' : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── TabRDA ───────────────────────────────────────────────────────────────────
const STATO_RDA_COLORS: Record<string, string> = {
  bozza: 'bg-slate-100 text-slate-600',
  approvata: 'bg-blue-100 text-blue-700',
  ordinata: 'bg-amber-100 text-amber-700',
  ricevuta: 'bg-green-100 text-green-700',
  annullata: 'bg-red-100 text-red-600',
}

function TabRDA() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const qc = useQueryClient()
  const { user } = useAuth()
  const [filtroStato, setFiltroStato] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [formData, setFormData] = useState({ articolo_id: '', fornitore_id: '', quantita_richiesta: '', prezzo_unitario_stimato: '', note: '' })

  const { data: rdas = [] } = useQuery({ queryKey: ['rda', filtroStato], queryFn: () => rdaApi.list(filtroStato || undefined).then(r => r.data) })
  const { data: articoli = [] } = useQuery({ queryKey: ['articoli-rda'], queryFn: () => magazzinoApi.articoli({}).then(r => r.data?.items ?? r.data) })
  const { data: fornitori = [] } = useQuery({ queryKey: ['fornitori'], queryFn: () => fornitoriApi.list().then(r => r.data) })

  const creaM = useMutation({
    mutationFn: (d: any) => rdaApi.create(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rda'] }); setShowCreate(false); toast.success('RDA creata') },
    onError: () => toast.error('Errore creazione RDA'),
  })
  const avanzaM = useMutation({
    mutationFn: ({ id, stato }: { id: number; stato: string }) => rdaApi.avanzaStato(id, stato),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rda'] }); toast.success('Stato aggiornato') },
    onError: () => toast.error('Errore aggiornamento stato'),
  })
  const eliminaM = useMutation({
    mutationFn: (id: number) => rdaApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rda'] }); toast.success('RDA eliminata') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const submitCrea = () => {
    if (!formData.articolo_id || !formData.quantita_richiesta) { toast.error('Articolo e quantità obbligatori'); return }
    creaM.mutate({
      articolo_id: parseInt(formData.articolo_id),
      fornitore_id: formData.fornitore_id ? parseInt(formData.fornitore_id) : null,
      quantita_richiesta: parseFloat(formData.quantita_richiesta),
      prezzo_unitario_stimato: formData.prezzo_unitario_stimato ? parseFloat(formData.prezzo_unitario_stimato) : null,
      note: formData.note || null,
    })
  }

  return (
    <div className="space-y-4">
      {ConfirmModal}
      <div className="flex flex-wrap gap-3 items-center">
        <select className="select" style={{ width: 180 }} value={filtroStato} onChange={e => setFiltroStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {['bozza', 'approvata', 'ordinata', 'ricevuta', 'annullata'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <button className="btn-primary text-sm ml-auto" onClick={() => setShowCreate(true)}>+ Nuova RDA</button>
      </div>

      {showCreate && (
        <div className="card border border-blue-200 bg-blue-50 space-y-3">
          <h4 className="font-semibold text-blue-800">Nuova Richiesta di Acquisto</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Articolo *</label>
              <select className="select w-full" value={formData.articolo_id} onChange={e => setFormData(p => ({ ...p, articolo_id: e.target.value }))}>
                <option value="">Seleziona articolo…</option>
                {(articoli as any[]).map((a: any) => <option key={a.id} value={a.id}>{a.codice} — {a.descrizione}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Fornitore</label>
              <select className="select w-full" value={formData.fornitore_id} onChange={e => setFormData(p => ({ ...p, fornitore_id: e.target.value }))}>
                <option value="">Nessun fornitore</option>
                {(fornitori as any[]).map((f: any) => <option key={f.id} value={f.id}>{f.ragione_sociale}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Quantità *</label>
              <input className="input w-full" type="number" min={0.1} step={0.1} value={formData.quantita_richiesta} onChange={e => setFormData(p => ({ ...p, quantita_richiesta: e.target.value }))} />
            </div>
            <div>
              <label className="label">Prezzo unitario stimato (€)</label>
              <input className="input w-full" type="number" min={0} step={0.01} value={formData.prezzo_unitario_stimato} onChange={e => setFormData(p => ({ ...p, prezzo_unitario_stimato: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className="label">Note</label>
              <input className="input w-full" value={formData.note} onChange={e => setFormData(p => ({ ...p, note: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary text-sm" onClick={submitCrea} disabled={creaM.isPending}>Crea RDA</button>
            <button className="btn-ghost text-sm" onClick={() => setShowCreate(false)}>Annulla</button>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200">
            <th className="text-left py-2 px-3 font-medium text-slate-600">Articolo</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Fornitore</th>
            <th className="text-right py-2 px-3 font-medium text-slate-600">Qtà</th>
            <th className="text-right py-2 px-3 font-medium text-slate-600">Valore stimato</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Stato</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Data</th>
            <th className="py-2 px-3"></th>
          </tr></thead>
          <tbody>
            {(rdas as any[]).map((r: any) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-3">
                  <div className="font-medium">{r.articolo_codice}</div>
                  <div className="text-xs text-slate-500">{r.articolo_descrizione}</div>
                </td>
                <td className="py-2 px-3 text-slate-600">{r.fornitore_nome ?? <span className="text-slate-300">—</span>}</td>
                <td className="py-2 px-3 text-right font-medium">{r.quantita_richiesta}</td>
                <td className="py-2 px-3 text-right">{r.valore_stimato != null ? `€ ${r.valore_stimato.toFixed(2)}` : '—'}</td>
                <td className="py-2 px-3">
                  <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', STATO_RDA_COLORS[r.stato] ?? 'bg-slate-100')}>{r.stato}</span>
                </td>
                <td className="py-2 px-3 text-slate-500">{r.data_richiesta ? new Date(r.data_richiesta).toLocaleDateString('it-IT') : '—'}</td>
                <td className="py-2 px-3">
                  <div className="flex gap-1 flex-wrap justify-end">
                    {(r.transizioni_possibili ?? []).map((s: string) => (
                      <button key={s} className={clsx('text-xs px-2 py-1 rounded font-medium', s === 'annullata' ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')}
                        onClick={() => { if (s === 'annullata' && !confirm('Annullare RDA?')) return; avanzaM.mutate({ id: r.id, stato: s }) }}>
                        → {s}
                      </button>
                    ))}
                    {['bozza', 'annullata'].includes(r.stato) && (
                      <button className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                        onClick={() => confirmDelete('Eliminare RDA?', () => eliminaM.mutate(r.id))}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(rdas as any[]).length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">Nessuna richiesta di acquisto</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── TabFornitori ─────────────────────────────────────────────────────────────
function TabFornitori() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico'].includes(user?.role ?? '')
  const [editId, setEditId] = useState<number | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const emptyForm = { ragione_sociale: '', piva: '', email: '', telefono: '', lead_time_giorni: '7', condizioni_pagamento: '', note: '', is_active: true }
  const [form, setForm] = useState<typeof emptyForm & { is_active: boolean }>(emptyForm)

  const { data: fornitori = [] } = useQuery({ queryKey: ['fornitori'], queryFn: () => fornitoriApi.list().then(r => r.data) })

  const startEdit = (f: any) => { setEditId(f.id); setForm({ ...f, lead_time_giorni: String(f.lead_time_giorni) }); setShowCreate(false) }
  const startCreate = () => { setEditId(null); setForm(emptyForm); setShowCreate(true) }
  const cancelEdit = () => { setEditId(null); setShowCreate(false) }

  const saveM = useMutation({
    mutationFn: (d: any) => editId ? fornitoriApi.update(editId, d) : fornitoriApi.create(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fornitori'] }); cancelEdit(); toast.success(editId ? 'Fornitore aggiornato' : 'Fornitore creato') },
    onError: () => toast.error('Errore salvataggio'),
  })
  const eliminaM = useMutation({
    mutationFn: (id: number) => fornitoriApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fornitori'] }); toast.success('Fornitore eliminato') },
    onError: () => toast.error('Errore eliminazione — potrebbe avere articoli associati'),
  })

  const submitForm = () => {
    if (!form.ragione_sociale.trim()) { toast.error('Ragione sociale obbligatoria'); return }
    saveM.mutate({ ...form, lead_time_giorni: parseInt(form.lead_time_giorni) || 7 })
  }

  const FormInline = () => (
    <div className="card border border-blue-200 bg-blue-50 space-y-3">
      <h4 className="font-semibold text-blue-800">{editId ? 'Modifica fornitore' : 'Nuovo fornitore'}</h4>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className="label">Ragione Sociale *</label><input className="input w-full" value={form.ragione_sociale} onChange={e => setForm(p => ({ ...p, ragione_sociale: e.target.value }))} /></div>
        <div><label className="label">P.IVA</label><input className="input w-full" value={form.piva} onChange={e => setForm(p => ({ ...p, piva: e.target.value }))} /></div>
        <div><label className="label">Email</label><input className="input w-full" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
        <div><label className="label">Telefono</label><input className="input w-full" value={form.telefono} onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))} /></div>
        <div><label className="label">Lead time (giorni)</label><input className="input w-full" type="number" min={1} value={form.lead_time_giorni} onChange={e => setForm(p => ({ ...p, lead_time_giorni: e.target.value }))} /></div>
        <div><label className="label">Condizioni pagamento</label><input className="input w-full" value={form.condizioni_pagamento} onChange={e => setForm(p => ({ ...p, condizioni_pagamento: e.target.value }))} /></div>
        <div className="col-span-2"><label className="label">Note</label><textarea className="input w-full" rows={2} value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} /></div>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary text-sm" onClick={submitForm} disabled={saveM.isPending}>Salva</button>
        <button className="btn-ghost text-sm" onClick={cancelEdit}>Annulla</button>
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      {ConfirmModal}
      {isAdmin && !showCreate && editId == null && (
        <div className="flex justify-end"><button className="btn-primary text-sm" onClick={startCreate}>+ Nuovo fornitore</button></div>
      )}
      {(showCreate || editId != null) && <FormInline />}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200">
            <th className="text-left py-2 px-3 font-medium text-slate-600">Ragione Sociale</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">P.IVA</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Email</th>
            <th className="text-left py-2 px-3 font-medium text-slate-600">Telefono</th>
            <th className="text-right py-2 px-3 font-medium text-slate-600">Lead time</th>
            <th className="py-2 px-3"></th>
          </tr></thead>
          <tbody>
            {(fornitori as any[]).map((f: any) => (
              <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-3 font-medium">{f.ragione_sociale}{!f.is_active && <span className="ml-2 text-xs text-slate-400">(inattivo)</span>}</td>
                <td className="py-2 px-3 font-mono text-xs">{f.piva ?? '—'}</td>
                <td className="py-2 px-3">{f.email ?? '—'}</td>
                <td className="py-2 px-3">{f.telefono ?? '—'}</td>
                <td className="py-2 px-3 text-right">{f.lead_time_giorni}gg</td>
                <td className="py-2 px-3">
                  {isAdmin && (
                    <div className="flex gap-1 justify-end">
                      <button className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200" onClick={() => startEdit(f)}><Edit size={12} /></button>
                      <button className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200" onClick={() => confirmDelete(`Eliminare ${f.ragione_sociale}?`, () => eliminaM.mutate(f.id))}><Trash2 size={12} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {(fornitori as any[]).length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Nessun fornitore registrato</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}
